import type { ActionDescriptor, TestRef } from '@krp/client-runtime'

import { PLATFORM_WINDOWS as W } from '../windows/registerWindows'

/**
 * Реестр действий пакета `platform-ui` (UI-ACTIONS-2a): что делает каждая
 * кнопка окон Platform — для подсказки ⓘ. Источник — аудит
 * `docs/plan/resource-platform/UI-ACTIONS.md` (таблицы окон Platform), плюс
 * окна раздела «Номенклатура» (UI-SECTIONS), появившиеся после аудита.
 *
 * Описания — словами магазина, без кодов прав и внутренних слов (это
 * проверяет тест реестра). Статус не пишется руками: он вычисляется из
 * доказательств. Руками — только «не работает» и «назначение уточняется», с
 * причиной. Тест — путь от корня репозитория и ТОЧНОЕ имя: тест реестра
 * находит каждый, иначе CI красный.
 */

const WEB = 'frontend/apps/web/src/tests'
const PKG = 'frontend/packages/platform-ui/src/features'
const KIT = 'frontend/packages/ui-kit/src/tests'
const E2E = 'tests/ResourcePlatform/E2E/specs'
const SRV = 'tests/ResourcePlatform'

const unit = (file: string, name: string): TestRef => ({ file, name })
const web = (file: string, name: string): TestRef => unit(`${WEB}/${file}`, name)
const pkg = (file: string, name: string): TestRef => unit(`${PKG}/${file}`, name)
const kit = (file: string, name: string): TestRef => unit(`${KIT}/${file}`, name)
const e2e = (file: string, name: string): TestRef => unit(`${E2E}/${file}`, name)
const srv = (file: string, name: string): TestRef => unit(`${SRV}/${file}`, name)

const ADMIN = 'Администратор реестра ресурсов.'
const SYSTEM_ADMIN = 'Только системный администратор.'
const SYNC_EDITOR = 'Тот, кому выдано право настраивать синхронизацию.'
const SYNC_READER = 'Тот, кому выдано право смотреть синхронизацию.'

const SOURCES_UI = 'resources/tests/ResourcesScreen.test.tsx'
const SOURCE_ADD_UI = 'resources/tests/SourceAddScreen.test.tsx'
const ADD_APP = 'Adding_a_resource_uses_the_native_form_and_the_secret_never_reaches_state_storage_or_log'

/** Вкладка «Источники»: список карточек без фильтров. Справка экрана одна (ⓘ сверху слева), записи нужны для описаний и доказательств. */
const RESOURCES: readonly ActionDescriptor[] = [
  {
    id: 'platform.resources.reload',
    windows: [W.resources],
    label: 'Обновить',
    summary: 'Перечитывает список подключённых баз и кабинетов с сервера.',
    effect: 'read',
    evidence: {
      unit: [pkg(SOURCES_UI, 'Sources_are_plain_cards_without_filters_and_the_buttons_call_actions')],
      server: [srv('Registry/RegistryAuthorizationTests.cs', 'Registry_list_does_not_expose_foreign_resources_or_credentials')],
    },
  },
  {
    id: 'platform.resources.add',
    windows: [W.resources],
    label: 'Добавить источник',
    summary:
      'Открывает окно добавления: сначала выберите вид источника плиткой, потом заполните форму. Сразу ничего не создаётся: источник появится, когда вы нажмёте «Создать».',
    effect: 'view',
    access: ADMIN,
    evidence: {
      unit: [
        pkg(SOURCES_UI, 'Sources_are_plain_cards_without_filters_and_the_buttons_call_actions'),
        web('resourceRegistry.test.tsx', ADD_APP),
      ],
      stand: [e2e('platform-flows.spec.ts', 'Resource_is_created_renamed_and_disabling_waits_for_the_native_service')],
    },
  },
  {
    id: 'platform.resources.add-disabled',
    windows: [W.resources],
    label: 'Добавить источник',
    summary: 'Подключать новые базы и кабинеты может только администратор. Кнопка показывает, что такое действие есть, но вам оно не выдано.',
    effect: 'view',
    access: ADMIN,
    disabledWhen: 'Всегда выключена: видна тем, у кого нет права подключать источники.',
    status: 'stub',
    statusReason: 'Кнопку нельзя нажать никогда — рядом строка с причиной, что именно не выдано.',
    evidence: { unit: [pkg(SOURCES_UI, 'Without_the_right_the_add_button_is_grey_and_says_why')], stand: [e2e('access.spec.ts', 'Seller_sees_one_onec_base_and_zero_aqsi_cabinets')] },
  },
  {
    id: 'platform.resources.open',
    windows: [W.resources],
    label: 'Открыть источник',
    summary: 'Открывает карточку источника поверх списка: его свойства, окна, проверку связи и диагностику.',
    effect: 'view',
    evidence: {
      unit: [web('app.test.tsx', 'Resource_window_opens_over_the_screen_with_rid_in_the_address_and_closes_back')],
      stand: [e2e('platform-flows.spec.ts', 'Resource_is_created_renamed_and_disabling_waits_for_the_native_service')],
    },
  },
]

/** Окно «Добавить источник» (решение владельца 24.10.2026): плитки видов, выбор ведёт к форме вида. */
const SOURCE_ADD: readonly ActionDescriptor[] = [
  {
    id: 'platform.source-add.pick',
    windows: [W.sourceAdd],
    label: 'Выбрать',
    summary: 'Открывает форму выбранного вида источника в этом же окне. Сразу ничего не создаётся и не записывается.',
    effect: 'view',
    access: ADMIN,
    evidence: {
      unit: [pkg(SOURCE_ADD_UI, 'Kinds_are_tiles_with_a_line_each_and_the_pick_goes_to_the_form'), web('resourceRegistry.test.tsx', ADD_APP)],
    },
  },
  {
    id: 'platform.source-add.cancel',
    windows: [W.sourceAdd],
    label: 'Отмена',
    summary: 'Закрывает окно добавления: ничего не создано, список остаётся как был.',
    effect: 'view',
    evidence: { unit: [pkg(SOURCE_ADD_UI, 'Kinds_are_tiles_with_a_line_each_and_the_pick_goes_to_the_form')] },
  },
]

const RESOURCE_CARD: readonly ActionDescriptor[] = [
  {
    id: 'platform.resource.entry',
    windows: [W.resource],
    label: 'Окно ресурса',
    summary: 'Открывает рабочее окно именно этого ресурса — товары, поставки, кассу, чеки и так далее.',
    effect: 'view',
    disabledWhen: 'Выключена, если право на это окно вам не выдано или выключено у ресурса.',
    evidence: {
      unit: [pkg('resources/tests/entries.test.tsx', 'Card_shows_only_the_entries_of_its_kind_and_explains_the_unavailable_one')],
      stand: [e2e('placement.spec.ts', 'Both_onec_bases_open_in_their_own_sections')],
    },
  },
  {
    id: 'platform.resource.edit',
    windows: [W.resource],
    label: 'Изменить',
    summary: 'Открывает форму этого подключения: название, что включено, адрес и прочие настройки.',
    effect: 'view',
    access: ADMIN,
    disabledWhen: 'Выключена без права менять реестр ресурсов.',
    evidence: {
      unit: [web('resourceRegistry.test.tsx', 'Non_admin_sees_registry_commands_unavailable_with_a_reason')],
      stand: [e2e('platform-flows.spec.ts', 'Resource_is_created_renamed_and_disabling_waits_for_the_native_service')],
    },
  },
  {
    id: 'platform.resource.probe',
    windows: [W.resource],
    label: 'Проверить связь',
    summary:
      'Один раз спрашивает внешнюю систему этого ресурса, отвечает ли она. Повторов нет: неверный пароль не перебирается.',
    effect: 'read-external',
    effectText: 'Один запрос к базе или кабинету; ничего не записывает.',
    disabledWhen: 'Выключена без права на проверку связи.',
    evidence: {
      unit: [web('resourceRegistry.test.tsx', 'Probe_result_is_shown_with_its_status_and_code')],
      server: [
        srv('Registry/ResourceLifecycleTests.cs', 'Onec_probe_goes_to_the_service_once_and_onec_disable_stays_refused_until_its_drain_exists'),
      ],
    },
  },
  {
    id: 'platform.resource.module-retry',
    windows: [W.resource],
    label: 'Повторить',
    summary:
      'Показана у модуля источника, который не ответил: ещё раз спрашивает внешнюю систему. Это та же проверка, что «Проверить связь»: один запрос, пароль не перебирается.',
    effect: 'read-external',
    effectText: 'Один запрос к базе или кабинету; ничего не записывает.',
    disabledWhen: 'Выключена без права на проверку связи.',
    evidence: {
      unit: [pkg('resources/tests/moduleState.test.tsx', 'Failed_module_shows_the_error_verbatim_with_its_code_and_retry_asks_again')],
      server: [
        srv('Registry/ResourceLifecycleTests.cs', 'Onec_probe_goes_to_the_service_once_and_onec_disable_stays_refused_until_its_drain_exists'),
      ],
    },
  },
  {
    id: 'platform.resource.close',
    windows: [W.resource],
    label: 'Закрыть',
    summary: 'Закрывает карточку и возвращает туда, откуда вы её открыли.',
    effect: 'view',
    evidence: { unit: [web('app.test.tsx', 'Resource_window_opens_over_the_screen_with_rid_in_the_address_and_closes_back')] },
  },
]

const EDITOR_WINDOWS = [W.resourceAdd, W.resourceEdit] as const

const RESOURCE_EDITOR: readonly ActionDescriptor[] = [
  {
    id: 'platform.resource-editor.enabled',
    windows: [W.resourceEdit],
    label: 'Ресурс включён',
    summary:
      'Включает или выключает ресурс после «Сохранить». Выключенным никто не работает; служба сначала доделывает начатое и только потом выключает.',
    effect: 'view',
    effectText: 'Само нажатие ничего не отправляет — изменение уходит кнопкой «Сохранить».',
    status: 'stub',
    statusReason: 'Для баз 1С выключение пока всегда отказывается: вывод базы из работы не сделан. Для кабинетов работает.',
    evidence: {
      unit: [pkg('resources/tests/ResourceEditorScreen.test.tsx', 'Create_requires_the_secret_and_edit_keeps_it_unchanged_when_empty')],
      stand: [
        e2e('platform-flows.spec.ts', 'Aqsi_resource_is_disabled_after_the_service_drains_it_and_enabled_back'),
        e2e('platform-flows.spec.ts', 'Resource_is_created_renamed_and_disabling_waits_for_the_native_service'),
      ],
    },
  },
  {
    id: 'platform.resource-editor.capability',
    windows: [...EDITOR_WINDOWS],
    label: 'Галочка права ресурса',
    summary: 'Включает у ресурса отдельную возможность — например, продажи или чтение каталога. Изменение уходит кнопкой «Сохранить».',
    effect: 'view',
    status: 'unclear',
    statusReason: 'Галочки подписаны служебными кодами, а не словами.',
    evidence: { unit: [pkg('resources/tests/ResourceEditorScreen.test.tsx', 'Submit_hands_the_secret_once_and_clears_the_field')] },
  },
  {
    id: 'platform.resource-editor.enable-all-capabilities',
    windows: [...EDITOR_WINDOWS],
    label: 'Включить все права',
    summary:
      'Отмечает в форме все права вида, выключенные у ресурса, — например, добавленные видом после создания ресурса. Показывается, пока хоть одно право выключено.',
    effect: 'view',
    effectText: 'Само нажатие ничего не отправляет: отметки видны в форме, изменение уходит кнопкой «Сохранить». Лишнее можно снять вручную.',
    access: ADMIN,
    evidence: { unit: [pkg('resources/tests/ResourceEditorScreen.test.tsx', 'Enable_all_capabilities_switches_on_every_right_in_the_draft_and_saves_nothing_by_itself')] },
  },
  {
    id: 'platform.resource-editor.save',
    windows: [...EDITOR_WINDOWS],
    label: 'Сохранить',
    summary:
      'Записывает подключение в реестр (у нового источника кнопка называется «Создать»). Пароль или ключ уходит отдельно и больше нигде не хранится — даже в форме он после отправки стирается.',
    effect: 'write-local',
    effectText: 'Пишет в реестр ресурсов нашего сервера; выключение кабинета ждёт, пока служба доделает начатое.',
    access: ADMIN,
    disabledWhen: 'Выключена без права менять реестр и пока идёт сохранение.',
    evidence: {
      unit: [
        pkg('resources/tests/ResourceEditorScreen.test.tsx', 'Submit_hands_the_secret_once_and_clears_the_field'),
        web('resourceRegistry.test.tsx', 'Adding_a_resource_uses_the_native_form_and_the_secret_never_reaches_state_storage_or_log'),
      ],
      server: [srv('Registry/RegistrySettingsHttpTests.cs', 'Create_and_update_keep_nested_onec_settings_through_http')],
      stand: [e2e('platform-flows.spec.ts', 'Resource_is_created_renamed_and_disabling_waits_for_the_native_service')],
    },
  },
  {
    id: 'platform.resource-editor.reload',
    windows: [...EDITOR_WINDOWS],
    label: 'Перечитать ресурс',
    summary:
      'Кто-то изменил этот ресурс, пока вы правили форму. Кнопка загружает свежую версию; ваши несохранённые правки придётся внести заново.',
    effect: 'read',
    disabledWhen: 'Видна только после такого конфликта.',
    evidence: { unit: [pkg('resources/tests/ResourceEditorScreen.test.tsx', 'Concurrent_change_offers_rereading_and_created_resource_is_reported_in_place')] },
  },
  {
    id: 'platform.resource-editor.back',
    windows: [W.resourceAdd],
    label: 'Назад к плиткам',
    summary: 'Возвращает к выбору вида источника. Введённое в форме не сохраняется: ничего не создано.',
    effect: 'view',
    evidence: { unit: [web('resourceRegistry.test.tsx', 'Form_goes_back_to_the_kind_tiles_without_creating_anything')] },
  },
  {
    id: 'platform.resource-editor.cancel',
    windows: [...EDITOR_WINDOWS],
    label: 'Отмена',
    summary: 'Закрывает форму без сохранения и возвращает туда, откуда вы пришли.',
    effect: 'view',
    evidence: { unit: [web('platformActions.test.tsx', 'Resource_form_cancel_closes_without_saving')] },
  },
  {
    id: 'platform.resource-editor.open-created',
    windows: [W.resourceAdd],
    label: 'Открыть карточку',
    summary: 'Ресурс создан — открывает его карточку вместо формы.',
    effect: 'view',
    evidence: {
      unit: [
        pkg('resources/tests/ResourceEditorScreen.test.tsx', 'Concurrent_change_offers_rereading_and_created_resource_is_reported_in_place'),
        web('resourceRegistry.test.tsx', 'Adding_a_resource_uses_the_native_form_and_the_secret_never_reaches_state_storage_or_log'),
      ],
    },
  },
]

const ACCESS: readonly ActionDescriptor[] = [
  {
    id: 'platform.access.role',
    windows: [W.access],
    label: 'Роль',
    summary: 'Выбирает роль, чьи ресурсы и права вы смотрите и меняете ниже.',
    effect: 'view',
    access: SYSTEM_ADMIN,
    evidence: { unit: [pkg('access/tests/access.test.tsx', 'Admin_changes_resource_grants_and_role_permissions_as_whole_sets')] },
  },
  {
    id: 'platform.access.mine-list',
    windows: [W.access],
    label: 'Список моих прав',
    summary: 'Раскрывает и прячет список всего, что сервер выдал вам, по группам и с кодами. Ничего не меняет.',
    effect: 'view',
    evidence: { unit: [pkg('access/tests/access.test.tsx', 'My_roles_and_rights_panel_names_the_roles_and_groups_the_rights_in_words')] },
  },
  {
    id: 'platform.access.resource-locked',
    windows: [W.access],
    label: 'Ресурс администратора',
    summary: 'Показывает, что администратор приложения допущен ко всем ресурсам сам. Галочку нельзя снять: менять нечего.',
    effect: 'view',
    access: SYSTEM_ADMIN,
    disabledWhen: 'Всегда выключена: у администратора приложения допуск к ресурсам не настраивается.',
    evidence: {
      unit: [pkg('access/tests/access.test.tsx', 'Super_role_shows_the_whole_catalog_checked_and_locked_without_save_buttons')],
    },
  },
  {
    id: 'platform.access.permission-locked',
    windows: [W.access],
    label: 'Право администратора',
    summary: 'Показывает, что у администратора приложения есть все права. Галочку нельзя снять: иначе экран прав мог бы стать недоступен всем.',
    effect: 'view',
    access: SYSTEM_ADMIN,
    disabledWhen: 'Всегда выключена: права администратора приложения не настраиваются.',
    evidence: {
      unit: [pkg('access/tests/access.test.tsx', 'Super_role_shows_the_whole_catalog_checked_and_locked_without_save_buttons')],
    },
  },
  {
    id: 'platform.access.resource-check',
    windows: [W.access],
    label: 'Ресурс роли',
    summary: 'Отмечает, с какими ресурсами может работать роль. Изменение уходит кнопкой «Сохранить допуск».',
    effect: 'view',
    access: SYSTEM_ADMIN,
    evidence: { unit: [pkg('access/tests/access.test.tsx', 'Admin_changes_resource_grants_and_role_permissions_as_whole_sets')] },
  },
  {
    id: 'platform.access.save-resources',
    windows: [W.access],
    label: 'Сохранить допуск',
    summary:
      'Записывает, с какими ресурсами работает роль, — весь список разом. Ресурсы, которых вам не видно, у роли остаются как были.',
    effect: 'write-local',
    effectText: 'Пишет допуск роли в нашу базу прав.',
    access: SYSTEM_ADMIN,
    disabledWhen: 'Выключена, пока список ресурсов не загрузился.',
    evidence: {
      unit: [
        web('resourceRegistry.test.tsx', 'Role_access_saves_the_chosen_resources_for_the_role'),
        web('resourceRegistry.test.tsx', 'Saving_role_access_keeps_grants_of_resources_outside_the_admin_list'),
      ],
    },
  },
  {
    id: 'platform.access.permission-check',
    windows: [W.access],
    label: 'Право роли',
    summary: 'Отмечает, что роль может делать. Изменение уходит кнопкой «Сохранить права».',
    effect: 'view',
    access: SYSTEM_ADMIN,
    evidence: { unit: [pkg('access/tests/access.test.tsx', 'Admin_changes_resource_grants_and_role_permissions_as_whole_sets')] },
  },
  {
    id: 'platform.access.permission-defaults',
    windows: [W.access],
    label: 'Подставить набор по умолчанию',
    summary: 'Отмечает галочки стартового набора прав этой роли. Ничего не сохраняет: проверьте и нажмите «Сохранить права».',
    effect: 'view',
    access: SYSTEM_ADMIN,
    disabledWhen: 'Выключена, пока набор роли не загрузился.',
    evidence: { unit: [pkg('access/tests/access.test.tsx', 'Role_defaults_fill_the_checkboxes_and_only_saving_writes_them')] },
  },
  {
    id: 'platform.access.save-permissions',
    windows: [W.access],
    label: 'Сохранить права',
    summary: 'Записывает права роли — весь набор разом. Люди с этой ролью получат их при следующем входе.',
    effect: 'write-local',
    effectText: 'Пишет набор прав роли в нашу базу прав.',
    access: SYSTEM_ADMIN,
    disabledWhen: 'Выключена, пока список прав не загрузился.',
    evidence: { unit: [pkg('access/tests/access.test.tsx', 'Admin_changes_resource_grants_and_role_permissions_as_whole_sets')] },
  },
]

const DIAGNOSTICS_AND_ABOUT: readonly ActionDescriptor[] = [
  {
    id: 'platform.diagnostics.reload',
    windows: [W.diagnostics],
    label: 'Обновить',
    summary: 'Перечитывает готовность шлюза и список ресурсов, где вам доступна диагностика.',
    effect: 'read',
    evidence: { unit: [web('resourceRegistry.test.tsx', 'Diagnostics_window_shows_gateway_readiness_and_only_diagnosable_resources')] },
  },
  {
    id: 'platform.diagnostics.open',
    windows: [W.diagnostics],
    label: 'Диагностика ресурса',
    summary: 'Открывает карточку ресурса — ту же, что из «Ресурсов»; диагностика службы — в ней внизу.',
    effect: 'view',
    status: 'unclear',
    statusReason: 'Окно повторяет «Ресурсы» — его сольют с «Ресурсами» или «О приложении».',
    evidence: { unit: [web('resourceRegistry.test.tsx', 'Diagnostics_window_shows_gateway_readiness_and_only_diagnosable_resources')] },
  },
  {
    id: 'platform.maintenance.apply',
    windows: [W.maintenance],
    label: 'Заблокировать кассы',
    summary:
      'Включает или выключает режим обновления касс. Когда он включён, кассы не начинают новые продажи: начатый чек кассир доводит, затем касса запирается. Окно показывает каждую открытую кассу и что на ней сейчас, — когда все заперты, можно начинать обновление.',
    effect: 'write-external',
    effectText: 'Меняет общий признак обновления на сервере — его сразу видят все кассы.',
    access: 'Администратор.',
    evidence: { unit: [web('maintenance.test.tsx', 'Panel_lists_open_registers_with_their_state_and_says_when_every_one_is_locked')] },
  },
  {
    id: 'platform.photo-settings.save',
    windows: [W.photoSettings],
    label: 'Сохранить',
    summary:
      'Записывает три числа: сколько суток живёт загруженное фото, пока его не отправили в источник; через сколько суток без обращения удаляются кадры поиска; и сколько места они могут занимать. Новый срок загрузки действует на фото, загруженные после сохранения; уже загруженные живут прежний срок.',
    effect: 'write-external',
    effectText: 'Меняет настройки на сервере — их сразу видят все.',
    access: 'Тот, кому выдано право настраивать раздел «Фото».',
    evidence: { unit: [web('photoSettings.test.tsx', 'Saving_sends_the_three_values_in_the_units_of_the_server_over_the_version_the_person_saw')] },
  },
  {
    id: 'platform.photo-settings.reload',
    windows: [W.photoSettings],
    label: 'Перечитать',
    summary: 'Заново читает настройки «Фото» с сервера и сбрасывает ошибку. Нужна, когда настройки изменил другой человек или чтение не удалось: введённые, но не сохранённые числа заменяются тем, что на сервере.',
    effect: 'read',
    evidence: { unit: [web('photoSettings.test.tsx', 'A_refusal_of_the_server_is_shown_in_its_words_with_a_way_to_reread')] },
  },
  {
    id: 'platform.about.reload',
    windows: [W.about],
    label: 'Обновить',
    summary: 'Заново спрашивает у шлюза версии всех служб, даты сборки и выкатки и какие из них сейчас отвечают.',
    effect: 'read',
    evidence: {
      unit: [web('ua1c.test.tsx', 'About_window_is_in_the_menu_and_shows_service_versions_from_the_gateway')],
      server: [srv('Auth/GatewayPassThroughTests.cs', 'About_lists_the_gateway_and_every_service_behind_it_without_addresses')],
    },
  },
]

const HW = 'hardware/tests/screen.test.tsx'
const hw = (name: string): TestRef => pkg(HW, name)

const HARDWARE: readonly ActionDescriptor[] = [
  {
    id: 'platform.hardware.recheck',
    windows: [W.hardware],
    label: 'Проверить сейчас',
    summary: 'Сразу заново ищет программу-драйвер на этом компьютере и спрашивает у неё, что установлено и отвечает ли касса. Окно и само перепроверяет, пока открыто.',
    effect: 'view',
    disabledWhen: 'Пока идёт проверка.',
    evidence: { unit: [hw('Missing_agent_asks_to_install_the_program_and_offers_no_setup_forms')] },
  },
  {
    id: 'platform.hardware.download-agent',
    windows: [W.hardware],
    label: 'Скачать программу-драйвер',
    summary: 'Открывает скачивание программы, которая связывает этот сайт с кассой и терминалом на вашем компьютере. Ставить её нужно один раз, дальше она работает в фоне.',
    effect: 'view',
    disabledWhen: 'Ссылка на установщик не задана — файл выдаёт администратор.',
    evidence: { unit: [hw('Missing_agent_asks_to_install_the_program_and_offers_no_setup_forms')] },
  },
  {
    id: 'platform.hardware.update-agent',
    windows: [W.hardware],
    label: 'Скачать новую версию',
    summary: 'Открывает скачивание новой версии программы-драйвера. Старая версия умеет не всё: без обновления часть вкладок будет недоступна.',
    effect: 'view',
    disabledWhen: 'Ссылка на установщик не задана — файл выдаёт администратор.',
    evidence: { unit: [hw('Outdated_agent_asks_to_update_and_offers_a_new_version')] },
  },
  {
    id: 'platform.hardware.agent-pick',
    windows: [W.hardware],
    label: 'Использовать',
    summary: 'На компьютере запущено несколько программ-драйверов (например, у двух пользователей Windows). Выбирает ту, с которой работает это окно; до выбора ничего не настраивается.',
    effect: 'view',
    disabledWhen: 'Эта программа уже выбрана или идёт проверка.',
    evidence: { live: [{ record: 'docs/plan/resource-platform/EQUIPMENT-WINDOW.md#7', date: '2026-10-17' }], unit: [hw('Several_agents_ask_for_a_choice_and_nothing_else')] },
  },
  {
    id: 'platform.hardware.copy-origin',
    windows: [W.hardware],
    label: 'Скопировать адрес сайта',
    summary: 'Копирует адрес этого сайта: его нужно добавить в настройки программы-драйвера, иначе она не пускает сайт к кассе и терминалу.',
    effect: 'view',
    evidence: { unit: [hw('Agent_that_refuses_the_site_shows_the_address_to_add')] },
  },
  {
    id: 'platform.hardware.next-step',
    windows: [W.hardware],
    label: 'Дальше',
    summary: 'Главный следующий шаг, чтобы начать продавать: подпись на кнопке меняется (скачать программу, скачать драйвер, найти кассу, найти терминал) и ведёт на нужную вкладку. Сама ничего не подключает.',
    effect: 'view',
    evidence: { live: [{ record: 'docs/plan/resource-platform/EQUIPMENT-WINDOW.md#7', date: '2026-10-17' }], unit: [hw('Next_step_button_opens_the_tab_of_the_step_and_runs_its_action'), hw('Find_kassa_step_only_offers_the_tab_and_does_not_scan_by_itself')] },
  },
  {
    id: 'platform.hardware.download-driver',
    windows: [W.hardware],
    label: 'Скачать драйвер АТОЛ',
    summary: 'Открывает страницу, откуда скачивается «Драйвер ККТ 10» от АТОЛ. Без него программа не сможет говорить с кассой. Поставили — вернитесь: окно заметит само.',
    effect: 'view',
    disabledWhen: 'Ссылка не задана — кнопки нет, а подсказка «что поставить» остаётся.',
    evidence: { unit: [hw('Missing_atol_driver_asks_to_download_it_and_the_next_step_points_to_drivers')] },
  },
  {
    id: 'platform.hardware.terminal-driver-download',
    windows: [W.hardware],
    label: 'Скачать драйвер',
    summary: 'Открывает страницу, откуда скачивается драйвер эквайрингового терминала. Без него программа не сможет говорить с терминалом.',
    effect: 'view',
    disabledWhen: 'Ссылка не задана — кнопки нет, а подсказка «что поставить» остаётся.',
    evidence: { unit: [hw('Missing_atol_driver_asks_to_download_it_and_the_next_step_points_to_drivers'), hw('Without_a_driver_link_there_is_no_download_button_but_the_advice_stays')] },
  },
  {
    id: 'platform.hardware.driver-check',
    windows: [W.hardware],
    label: 'Проверить',
    summary: 'Заново спрашивает у программы-драйвера, установлены ли драйверы и запущены ли они. Ничего не меняет.',
    effect: 'read',
    disabledWhen: 'Пока идёт проверка.',
    evidence: { unit: [hw('Missing_atol_driver_asks_to_download_it_and_the_next_step_points_to_drivers')] },
  },
  {
    id: 'platform.hardware.copy-diagnostics',
    windows: [W.hardware],
    label: 'Скопировать сведения',
    summary: 'Копирует сведения для поддержки: версия программы, порт, состояние драйверов. Паролей и ключей в них нет.',
    effect: 'view',
    evidence: { unit: [hw('Other_equipment_and_support_info_are_folded_and_the_info_can_be_copied')] },
  },
  {
    id: 'platform.hardware.toggle',
    windows: [W.hardware],
    label: 'Показать или скрыть',
    summary: 'Раскрывает или сворачивает дополнительные сведения: подсказки, если программа не находится, сведения об агенте, форму ручного подключения.',
    effect: 'view',
    evidence: { unit: [hw('Other_equipment_and_support_info_are_folded_and_the_info_can_be_copied'), hw('Connected_agent_details_show_version_port_and_run_mode')] },
  },
  {
    id: 'platform.hardware.tabs',
    windows: [W.hardware],
    label: 'Вкладки оборудования',
    summary: 'Драйверы, Кассы, Терминалы. Открытая вкладка видна в адресной строке: обновление страницы и кнопка «назад» браузера её не теряют. Само окно вкладки не переключает.',
    effect: 'view',
    evidence: { live: [{ record: 'docs/plan/resource-platform/EQUIPMENT-WINDOW.md#7', date: '2026-10-17' }], unit: [hw('Tab_click_asks_to_change_the_address_and_marks_the_open_tab')] },
  },
  {
    id: 'platform.hardware.kassa-scan',
    windows: [W.hardware],
    label: 'Найти кассу',
    summary: 'Просит программу-драйвер найти кассу, подключённую к этому компьютеру. Ничего не подключает и не сохраняет, чек, смену и оплату не создаёт.',
    effect: 'read',
    disabledWhen: 'Нет связи с программой-драйвером, не установлен драйвер ККТ 10 или сканирование уже идёт.',
    evidence: {
      unit: [hw('Kassa_without_a_device_offers_a_scan_with_com_ports_and_says_what_to_check'), hw('Scan_button_explains_the_missing_driver_in_the_info_card')],
    },
  },
  {
    id: 'platform.hardware.kassa-scan-com',
    windows: [W.hardware],
    label: 'Проверить порт',
    summary: 'Проверяет, не подключена ли касса к выбранному COM-порту. Порты сами не опрашиваются: проверка идёт только по вашей просьбе.',
    effect: 'read',
    disabledWhen: 'Порт не выбран или нет связи с программой-драйвером.',
    evidence: { unit: [hw('Kassa_without_a_device_offers_a_scan_with_com_ports_and_says_what_to_check'), hw('Without_known_com_ports_the_port_is_typed_by_hand')] },
  },
  {
    id: 'platform.hardware.kassa-connect',
    windows: [W.hardware],
    label: 'Подключить кассу',
    summary: 'Говорит программе-драйверу, какая у вас касса и как она подключена, включает её и сразу проверяет связь. Если касса уже есть, предложит заменить её.',
    effect: 'device',
    effectText: 'Запоминается на этом компьютере: после перезагрузки касса включится сама. Чек и смена не создаются.',
    disabledWhen: 'Идёт другое действие или нет связи с программой-драйвером.',
    evidence: {
      unit: [
        hw('Ready_candidate_is_connected_in_one_click_with_the_suggested_values'),
        hw('Second_kassa_candidate_asks_to_confirm_the_replacement'),
      ],
    },
  },
  {
    id: 'platform.hardware.kassa-test',
    windows: [W.hardware],
    label: 'Проверить связь',
    summary: 'Один раз спрашивает кассу, отвечает ли она, и показывает модель и номер. Чек не пробивает, смену не открывает.',
    effect: 'read',
    disabledWhen: 'Касса не включена, идёт другое действие или нет связи с программой-драйвером.',
    evidence: { live: [{ record: 'docs/plan/resource-platform/EQUIPMENT-WINDOW.md#7', date: '2026-10-17' }], unit: [hw('Working_kassa_with_a_closed_shift_blocks_selling_and_points_to_the_rmk')] },
  },
  {
    id: 'platform.hardware.atol-model',
    windows: [W.hardware],
    label: 'Модель кассы',
    summary: 'Какая у вас касса. Если не уверены, оставьте «Определить автоматически» — драйвер найдёт подключённую сам.',
    effect: 'view',
    evidence: { unit: [hw('Manual_form_sends_the_chosen_model_and_port_and_com_needs_a_name')] },
  },
  {
    id: 'platform.hardware.atol-port',
    windows: [W.hardware],
    label: 'Подключение',
    summary: 'Как касса подключена к компьютеру: обычно по USB-кабелю; COM-порт нужен, только если так настроена касса.',
    effect: 'view',
    evidence: { unit: [hw('Manual_form_sends_the_chosen_model_and_port_and_com_needs_a_name')] },
  },
  {
    id: 'platform.hardware.atol-com',
    windows: [W.hardware],
    label: 'COM-порт кассы',
    summary: 'Имя COM-порта, к которому подключена касса, например COM3. Список портов компьютера показывается, если программа-драйвер его сообщила.',
    effect: 'view',
    evidence: { unit: [hw('Manual_form_sends_the_chosen_model_and_port_and_com_needs_a_name'), hw('Without_known_com_ports_the_port_is_typed_by_hand')] },
  },
  {
    id: 'platform.hardware.enable-atol',
    windows: [W.hardware],
    label: 'Включить кассу',
    summary: 'Говорит программе-драйверу на этом компьютере, какая у вас касса и как она подключена, и включает её сразу, без перезапуска.',
    effect: 'device',
    effectText: 'Запоминается на этом компьютере: после перезагрузки касса включится сама. Чек и смена не создаются.',
    disabledWhen: 'Для подключения по COM нужно указать имя порта; пока идёт применение — подождать.',
    evidence: { live: [{ record: 'docs/plan/resource-platform/EQUIPMENT-WINDOW.md#7', date: '2026-10-17' }], unit: [hw('Manual_form_sends_the_chosen_model_and_port_and_com_needs_a_name')] },
  },
  {
    id: 'platform.hardware.disable-atol',
    windows: [W.hardware],
    label: 'Выключить кассу',
    summary: 'Отключает кассу в программе-драйвере на этом компьютере: продажа через неё станет невозможной, пока вы её снова не включите.',
    effect: 'device',
    effectText: 'Запоминается на этом компьютере.',
    disabledWhen: 'Идёт другое действие или нет связи с программой-драйвером.',
    evidence: {
      live: [{ record: 'docs/plan/resource-platform/EQUIPMENT-WINDOW.md#7', date: '2026-10-17' }],
      unit: [hw('Working_kassa_with_a_closed_shift_blocks_selling_and_points_to_the_rmk'), hw('Disabled_button_names_its_reason_in_the_info_card')],
    },
  },
  {
    id: 'platform.hardware.terminal-scan',
    windows: [W.hardware],
    label: 'Найти терминал',
    summary: 'Просит программу-драйвер найти эквайринговые терминалы на этом компьютере и в сети. Ничего не сохраняет и платежей не делает.',
    effect: 'read',
    disabledWhen: 'Нет связи с программой-драйвером, не установлен драйвер терминала или сканирование уже идёт.',
    evidence: { live: [{ record: 'docs/plan/resource-platform/EQUIPMENT-WINDOW.md#7', date: '2026-10-17' }], unit: [hw('Scan_button_asks_for_a_terminal_scan')] },
  },
  {
    id: 'platform.hardware.terminal-field',
    windows: [W.hardware],
    label: 'Поле терминала',
    summary: 'Значение, которое не удалось определить само, например идентификатор терминала от банка. Без него терминал не подключить.',
    effect: 'view',
    evidence: { unit: [hw('Terminal_candidate_asks_for_the_missing_field_and_connects_with_it')] },
  },
  {
    id: 'platform.hardware.terminal-save',
    windows: [W.hardware],
    label: 'Подключить',
    summary: 'Запоминает найденный терминал в программе-драйвере на этом компьютере и один раз проверяет связь. Платёж не проводится.',
    effect: 'device',
    effectText: 'Запоминается на этом компьютере.',
    disabledWhen: 'Не заполнено обязательное поле или идёт другое действие.',
    evidence: { unit: [hw('Terminal_candidate_asks_for_the_missing_field_and_connects_with_it'), hw('Ready_terminal_candidate_is_connected_in_one_click')] },
  },
  {
    id: 'platform.hardware.terminal-test',
    windows: [W.hardware],
    label: 'Проверить связь',
    summary: 'Один раз спрашивает терминал, на связи ли он. Денег не списывает. Автоматически связь с терминалом не проверяется.',
    effect: 'read',
    disabledWhen: 'Идёт другое действие или нет связи с программой-драйвером.',
    evidence: { unit: [hw('Saved_terminal_says_the_link_was_not_checked_and_never_ready_without_a_check')] },
  },
  {
    id: 'platform.hardware.terminal-activate',
    windows: [W.hardware],
    label: 'Сделать основным',
    summary: 'Назначает этот терминал основным: оплата картой пойдёт через него, остальные станут запасными.',
    effect: 'device',
    effectText: 'Запоминается на этом компьютере.',
    disabledWhen: 'Терминал уже основной или идёт другое действие.',
    evidence: { unit: [hw('Second_terminal_can_be_made_main_and_removal_asks_for_confirmation')] },
  },
  {
    id: 'platform.hardware.terminal-remove',
    windows: [W.hardware],
    label: 'Удалить',
    summary: 'Убирает терминал из программы-драйвера на этом компьютере после подтверждения. Оплата картой на этом компьютере перестанет работать. Сам терминал и банковские настройки не затрагиваются.',
    effect: 'device',
    effectText: 'Запоминается на этом компьютере.',
    disabledWhen: 'Идёт другое действие. Терминал нельзя удалить, пока по нему есть платёж с неизвестным итогом.',
    evidence: { unit: [hw('Second_terminal_can_be_made_main_and_removal_asks_for_confirmation')] },
  },
  {
    id: 'platform.hardware.scan-cancel',
    windows: [W.hardware],
    label: 'Отмена',
    summary: 'Перестаёт ждать ответ сканирования. Программа-драйвер дочитает проверку сама и отпустит порт; ничего не сохраняется.',
    effect: 'view',
    evidence: { unit: [hw('Scan_in_progress_shows_a_row_per_driver_and_a_cancel_button')] },
  },
  {
    id: 'platform.hardware.confirm-cancel',
    windows: [W.hardware],
    label: 'Не надо',
    summary: 'Закрывает вопрос о подтверждении и ничего не меняет.',
    effect: 'view',
    evidence: { unit: [hw('Second_kassa_candidate_asks_to_confirm_the_replacement')] },
  },
]

const LINKS: readonly ActionDescriptor[] = [
  {
    id: 'platform.links.reload',
    windows: [W.catalogLinks],
    label: 'Обновить',
    summary: 'Перечитывает список ресурсов и связанных товаров.',
    effect: 'read',
    access: SYNC_READER,
    evidence: { unit: [pkg('catalog-links/tests/catalogLinks.test.tsx', 'Linked_products_show_resource_names_keys_and_how_many_links_are_hidden')] },
  },
  {
    id: 'platform.links.first-resource',
    windows: [W.catalogLinks],
    label: 'Ресурс первой карточки',
    summary: 'База или кабинет, где лежит первая из связываемых карточек.',
    effect: 'view',
    evidence: { stand: [e2e('platform-flows.spec.ts', 'Catalog_links_connect_a_onec_product_to_an_aqsi_product')] },
  },
  {
    id: 'platform.links.second-resource',
    windows: [W.catalogLinks],
    label: 'Ресурс второй карточки',
    summary: 'База или кабинет, где лежит вторая карточка — та, что станет тем же товаром.',
    effect: 'view',
    evidence: { stand: [e2e('platform-flows.spec.ts', 'Catalog_links_connect_a_onec_product_to_an_aqsi_product')] },
  },
  {
    id: 'platform.links.link',
    windows: [W.catalogLinks],
    label: 'Связать',
    summary:
      'Объявляет две карточки разных ресурсов одним товаром. После этого обмен передаёт изменения между ними; одинаковое название связью не считается.',
    effect: 'write-local',
    effectText: 'Пишет связь в нашу базу; карточки в базах и кабинетах не меняются.',
    access: SYNC_EDITOR,
    disabledWhen: 'Выключена, пока не выбраны обе карточки двух разных ресурсов и не написана причина.',
    status: 'unclear',
    statusReason: 'Ключи карточек нужно вписывать руками — поиска по названию или штрихкоду пока нет.',
    evidence: {
      unit: [pkg('catalog-links/tests/catalogLinks.test.tsx', 'Link_button_is_disabled_with_the_reason_and_sends_only_when_allowed')],
      server: [
        srv('Catalog/CatalogIdentityTests.cs', 'Linking_refuses_a_taken_key_a_second_card_in_the_same_resource_and_a_disabled_resource'),
      ],
      stand: [e2e('platform-flows.spec.ts', 'Catalog_links_connect_a_onec_product_to_an_aqsi_product')],
    },
  },
  {
    id: 'platform.links.list-resource',
    windows: [W.catalogLinks],
    label: 'Ресурс списка',
    summary: 'Показывает связанные товары выбранной базы или кабинета.',
    effect: 'read',
    evidence: { stand: [e2e('platform-flows.spec.ts', 'Catalog_links_connect_a_onec_product_to_an_aqsi_product')] },
  },
  {
    id: 'platform.links.unlink',
    windows: [W.catalogLinks],
    label: 'Разорвать связь',
    summary:
      'Разрывает связь карточки с общим товаром — в два нажатия и с причиной («Не разрывать» — передумать). Сами карточки не меняются; обмен перестаёт передавать между ними.',
    effect: 'write-local',
    effectText: 'Пишет разрыв и причину в журнал связей нашей базы.',
    access: SYNC_EDITOR,
    disabledWhen: '«Подтвердить разрыв» выключена, пока не написана причина.',
    evidence: {
      unit: [pkg('catalog-links/tests/catalogLinks.test.tsx', 'Linked_products_show_resource_names_keys_and_how_many_links_are_hidden')],
      server: [srv('Catalog/CatalogIdentityTests.cs', 'Link_and_unlink_are_audited_and_unlink_needs_no_pending_commands')],
    },
  },
]

const SEARCH: readonly ActionDescriptor[] = [
  {
    id: 'platform.search.service',
    windows: [W.search],
    label: 'Настройка поиска',
    summary: 'Раскрывает звенья поиска по фото, и проверку их состояния. Нужно для настройки, а не для работы магазина.',
    effect: 'view',
    access: 'Тот, кому выдано право «Поиск по фото: тестовый каталог замеров»; у системного администратора оно есть всегда.',
    evidence: { unit: [web('search.test.tsx', 'Health_is_one_word_for_everyone_and_service_details_only_with_the_testing_right')] },
  },
  {
    id: 'platform.search.health',
    windows: [W.search],
    label: 'Проверить состояние',
    summary: 'Спрашивает, работает ли сейчас поиск по фото и все его звенья. Кнопка — в свёрнутом блоке «Настройка поиска».',
    effect: 'read',
    access: 'Тот, кому выдано право «Поиск по фото: тестовый каталог замеров»; у системного администратора оно есть всегда.',
    evidence: {
      unit: [
        web('search.test.tsx', 'Health_degraded_is_shown_separately_from_the_search_result'),
        web('search.test.tsx', 'Health_is_one_word_for_everyone_and_service_details_only_with_the_testing_right'),
      ],
      server: [srv('Search/FacadeBehaviorTests.cs', 'Health_is_ok_only_when_both_services_are_ready_and_the_encoders_match')],
    },
  },
  {
    id: 'platform.search.scanner',
    windows: [W.search],
    label: 'Сканер',
    summary: 'Открывает камеру во весь экран: штрихкод читается сам и ищется в общем каталоге, а без штрихкода товар можно снять и найти по фото. Найденное — под камерой.',
    effect: 'view',
    disabledWhen: 'Выключена, пока снимок готовится или поиск уже идёт. Если на устройстве нет камеры, кнопка остаётся на месте, серая и с подписью «камеры нет», — выберите фото.',
    evidence: {
      unit: [
        web('search.test.tsx', 'Search_offers_the_live_scanner_and_the_gallery'),
        web('search.test.tsx', 'Without_a_camera_the_scanner_button_stays_visible_but_disabled_with_a_reason'),
      ],
    },
  },
  {
    id: 'platform.search.scanner-close',
    windows: [W.scanner],
    label: 'Назад',
    summary: 'Закрывает сканер и выключает камеру. Последняя выдача по фото остаётся на экране поиска.',
    effect: 'view',
    evidence: { unit: [web('search.test.tsx', 'Scanner_photo_result_stays_on_the_search_screen_after_closing')] },
  },
  {
    id: 'platform.search.scanner-shoot',
    windows: [W.scanner],
    label: 'Снять',
    summary: 'Белая кнопка внизу: снимает кадр с камеры и ищет товар по фото — когда штрихкода нет или он не нашёлся. Найденное выезжает списком снизу.',
    effect: 'read',
    effectText: 'Отправляет снимок на распознавание и только читает каталог.',
    disabledWhen: 'Выключена, пока камера не работает или поиск по прошлому снимку ещё идёт.',
    evidence: {
      unit: [
        web('search.test.tsx', 'Scanner_shot_goes_to_photo_search_and_hits_show_below_the_camera'),
        web('search.test.tsx', 'Scanner_is_full_screen_with_a_white_shutter_and_results_slide_up_from_below'),
      ],
    },
  },
  {
    id: 'platform.search.scanner-file',
    windows: [W.scanner],
    label: 'Выбрать фото',
    summary: 'Выбирает снимок из галереи или файлов и ищет по нему — например, когда камера недоступна.',
    effect: 'read',
    effectText: 'Отправляет снимок на распознавание и только читает каталог.',
    disabledWhen: 'Выключена, пока поиск уже идёт.',
    evidence: { unit: [web('search.test.tsx', 'Without_a_camera_the_scanner_explains_and_offers_the_gallery')] },
  },
  {
    id: 'platform.search.scanner-sheet-close',
    windows: [W.scanner],
    label: 'Скрыть',
    summary: 'Убирает список найденного вниз — камера снова видна целиком и читает штрихкоды. Список можно вернуть кнопкой «Найденное».',
    effect: 'view',
    gestureOf: 'смахнуть список вниз за его шапку',
    evidence: {
      unit: [
        web('search.test.tsx', 'Scanner_is_full_screen_with_a_white_shutter_and_results_slide_up_from_below'),
        web('search.test.tsx', 'A_new_barcode_after_hiding_the_sheet_reopens_it_with_the_new_product'),
      ],
    },
  },
  {
    id: 'platform.search.scanner-sheet-open',
    windows: [W.scanner],
    label: 'Найденное',
    summary: 'Снова показывает список найденного по последнему штрихкоду или снимку.',
    effect: 'view',
    evidence: { unit: [web('search.test.tsx', 'Scanner_is_full_screen_with_a_white_shutter_and_results_slide_up_from_below')] },
  },
  {
    id: 'platform.search.product-open',
    windows: [W.search, W.scanner],
    label: 'Карточка товара',
    summary: 'Нажатие на плашку открывает общую карточку товара поверх поиска или сканера; нажатие на фото — фото на весь экран. Закрыли — вы там же, где были.',
    effect: 'view',
    evidence: {
      unit: [
        web('search.test.tsx', 'Found_product_photo_opens_full_screen_and_the_tile_opens_the_common_product_card'),
        web('search.test.tsx', 'Scanner_barcode_tile_opens_the_common_product_card_over_the_scanner'),
      ],
    },
  },
  {
    id: 'platform.search.sent-shot',
    windows: [W.search, W.scanner],
    label: 'Отправленный снимок',
    summary: 'Вверху выдачи слева — снимок, ушедший в поиск, справа — прочитанный на нём текст. Нажатие на снимок открывает его на весь экран: видно, что снялось и какого качества кадр.',
    effect: 'view',
    evidence: {
      unit: [
        web('search.test.tsx', 'The_sent_shot_and_its_read_text_head_the_results_and_the_shot_opens_full_screen'),
        web('search.test.tsx', 'Read_text_sits_right_of_the_shot_as_compact_small_text_without_scroll'),
      ],
    },
  },
  {
    id: 'platform.search.file',
    windows: [W.search],
    label: 'Выбрать фото',
    summary: 'Выбирает снимок из галереи или файлов; поиск начинается сразу. Большой снимок перед отправкой уменьшается.',
    effect: 'read',
    effectText: 'Отправляет снимок на распознавание и только читает каталог.',
    disabledWhen: 'Выключена, пока снимок готовится или поиск уже идёт.',
    evidence: { unit: [web('search.test.tsx', 'Too_big_or_non_image_file_is_refused_before_sending')] },
  },
  {
    id: 'platform.search.progress-details',
    windows: [W.search],
    label: 'Посмотреть детали поиска',
    summary:
      'Ссылка в строке «Найдено за … с»: раскрывает или прячет детали законченного поиска. Сначала «Как обработано фото» — рядом исходный снимок, кадр без фона (если фон убирался) и то, что реально увидел энкодер, ниже подпись «фон убран / не убирался» с причиной; затем разбор обычными словами — загрузка снимка, чтение текста, поиск по тексту, поиск по виду, объединение результатов, сколько заняла каждая часть и что нашлось. Любую картинку из деталей можно нажать и рассмотреть на весь экран.',
    effect: 'view',
    access: 'Только администратор системы (роль «Администратор приложения»); у остальных ссылки и деталей (вместе с обработкой фото) в разметке нет.',
    evidence: {
      unit: [
        pkg('search/tests/searchProgress.test.tsx', 'A_system_admin_sees_the_details_link_and_it_opens_plain_steps'),
        pkg('search/tests/searchProgress.test.tsx', 'A_non_admin_sees_found_in_time_but_no_details_link_and_no_details_block_at_all'),
        pkg('search/tests/searchProgress.test.tsx', 'How_the_photo_was_processed_is_a_section_of_the_admin_details_and_not_a_link_in_the_time_line'),
        pkg('search/tests/searchProcessing.test.tsx', 'The_processing_section_lives_inside_the_search_details_with_the_original_the_processed_frame_and_what_the_encoder_saw'),
        pkg('search/tests/searchProcessing.test.tsx', 'The_found_in_time_line_has_no_separate_processing_link_and_a_non_admin_never_sees_the_processing'),
        web('search.test.tsx', 'Stage_events_and_weighted_words_are_shown_after_the_search'),
      ],
    },
  },
  {
    id: 'platform.search.photo-zoom',
    windows: [W.search],
    label: 'Увеличить картинку',
    summary:
      'Нажатие на любую картинку в деталях поиска (исходный снимок, кадр без фона, то, что видит энкодер) открывает её на весь экран с подписью, что это за картинка. Приблизить можно щипком, двойным тапом, колесом мыши или кнопками «+», «−» и «По размеру»; приближенную картинку двигают пальцем или мышью. Закрыть — кнопка «Закрыть», клавиша Esc, тап мимо картинки или смах вниз; фокус возвращается на картинку. Не загрузилась — вместо пустоты слова об этом.',
    effect: 'view',
    access: 'Только администратор системы: картинки есть только в деталях поиска.',
    evidence: {
      unit: [
        pkg('search/tests/searchProcessing.test.tsx', 'Tapping_a_processing_frame_opens_it_full_screen_with_a_caption_and_zoom_buttons'),
        kit('photoViewer.test.tsx', 'Tapping_the_picture_opens_it_full_screen_with_a_caption_saying_what_it_is'),
        kit('photoViewer.test.tsx', 'Zoom_buttons_zoom_in_zoom_out_and_return_to_fit'),
        kit('photoViewer.test.tsx', 'The_mouse_wheel_and_plus_minus_keys_zoom_the_picture'),
        kit('photoViewer.test.tsx', 'Escape_closes_the_viewer_and_focus_returns_to_the_picture'),
        kit('photoViewer.test.tsx', 'The_close_button_and_a_tap_on_the_dim_background_close_the_viewer'),
        kit('photoViewer.test.tsx', 'A_picture_that_fails_to_load_shows_a_readable_message_instead_of_emptiness'),
      ],
    },
  },
  {
    id: 'platform.search.cancel',
    windows: [W.search, W.scanner],
    label: 'Отменить',
    summary: 'Останавливает идущий поиск: загрузка снимка обрывается, сервер прекращает работу, где это возможно. Результата не будет, но и ошибкой это не считается.',
    effect: 'view',
    disabledWhen: 'Видна только пока поиск идёт.',
    evidence: {
      unit: [web('search.test.tsx', 'Cancel_stops_the_running_search_and_says_it_was_cancelled')],
      server: [srv('Search/PhotoSearchStreamTests.cs', 'Cancelling_the_request_stops_the_work_down_to_the_text_service_and_ends_without_a_result')],
    },
  },
  {
    id: 'platform.search.retry',
    windows: [W.search, W.scanner],
    label: 'Повторить',
    summary: 'Ищет ещё раз по тому же снимку, не заставляя снимать или выбирать его заново. Сервер снимок не хранит, поэтому он отправляется снова.',
    effect: 'read',
    effectText: 'Отправляет тот же снимок на распознавание и только читает каталог.',
    disabledWhen: 'Видна после ошибки поиска, которую повтор может исправить (служба недоступна, нет связи, таймаут), и после пустого итога.',
    evidence: {
      unit: [web('search.test.tsx', 'A_failed_search_offers_retry_with_the_same_photo_and_the_second_try_succeeds')],
    },
  },
  {
    id: 'platform.search.pick-another',
    windows: [W.search, W.scanner],
    label: 'Выбрать другое фото',
    summary: 'Открывает выбор снимка из галереи или файлов; выбранный снимок сразу уходит в поиск вместо прежнего.',
    effect: 'read',
    effectText: 'Отправляет новый снимок на распознавание и только читает каталог.',
    evidence: {
      unit: [web('search.test.tsx', 'A_failed_search_offers_retry_with_the_same_photo_and_the_second_try_succeeds')],
    },
  },
]

const COMMON_CATALOG: readonly ActionDescriptor[] = [
  {
    id: 'platform.catalog.reload',
    windows: [W.nomenclature],
    label: 'Обновить',
    summary: 'Перечитывает товары общего каталога и их связи с источниками.',
    effect: 'read',
    evidence: {
      unit: [pkg('catalog/tests/commonCatalog.test.tsx', 'Rows_show_sources_by_resource_name_and_unlinked_products_say_so')],
      server: [
        srv('Catalog/CommonCatalogApiTests.cs', 'Person_sees_products_with_links_only_to_resources_where_catalog_read_is_allowed'),
      ],
    },
  },
  {
    id: 'platform.catalog.sources',
    windows: [W.nomenclature],
    label: 'Источники',
    summary:
      'Раскрывает и сворачивает список источников каталога: что это за база или кабинет, сколько в нём товаров и когда его в последний раз сверяли. Ничего не меняет.',
    effect: 'view',
    evidence: {
      unit: [web('sections.test.tsx', 'Sources_strip_lists_every_kind_of_source_but_only_aqsi_can_be_checked_for_freshness')],
    },
  },
  {
    id: 'platform.catalog.sources.check',
    windows: [W.nomenclature],
    label: 'Проверить актуальность',
    summary:
      'Спрашивает кабинет источника прямо сейчас: не появились ли новые или изменённые товары. Если проверка уже идёт или прошла меньше двух минут назад, запроса не будет, и это будет сказано.',
    effect: 'read',
    disabledWhen: 'Выключена, пока идёт проверка этого источника.',
    evidence: {
      unit: [pkg('catalog/tests/sourcesStrip.test.tsx', 'The_strip_shows_each_source_with_count_last_check_and_result_and_checks_on_demand')],
      server: [
        unit(
          'tests/Resource.Aqsi/Freshness/AqsiFreshnessProbeTests.cs',
          'The_button_checks_now_a_second_press_inside_the_gap_does_not_touch_the_cabinet_and_the_strip_shows_the_result',
        ),
      ],
    },
  },
  {
    id: 'platform.catalog.sources.check-full',
    windows: [W.nomenclature],
    label: 'Проверить полностью',
    summary:
      'Перечитывает весь кабинет источника, а не только то, что изменилось: найдёт и пропавшие товары. Дольше обычной проверки; идёт через те же правила, что и «Проверить актуальность».',
    effect: 'read',
    disabledWhen: 'Выключена, пока идёт проверка этого источника.',
    evidence: {
      unit: [pkg('catalog/tests/sourcesStrip.test.tsx', 'The_strip_shows_each_source_with_count_last_check_and_result_and_checks_on_demand')],
      server: [
        unit(
          'tests/Resource.Aqsi/Freshness/AqsiFreshnessProbeTests.cs',
          'The_full_reconcile_is_done_when_its_term_is_over_by_request_or_when_there_is_no_snapshot_yet',
        ),
      ],
    },
  },
  {
    id: 'platform.catalog.page',
    windows: [W.nomenclature],
    label: 'Страница каталога',
    summary: 'Листает каталог: «Назад» — предыдущие товары, «Дальше» — следующие. Пока страница грузится, видны прежние строки.',
    effect: 'read',
    disabledWhen: 'Выключена на первой и на последней странице.',
    evidence: {
      unit: [pkg('catalog/tests/commonCatalog.test.tsx', 'Table_shows_sources_unlinked_state_and_pages_and_opens_links_of_the_product')],
      server: [srv('Catalog/CommonCatalogApiTests.cs', 'Pages_follow_the_name_order_and_report_the_total')],
    },
  },
  {
    id: 'platform.catalog.links',
    windows: [W.nomenclature],
    label: 'Связи',
    summary: 'Открывает связи этого товара: в каких базах и кабинетах лежат его карточки.',
    effect: 'view',
    evidence: {
      unit: [pkg('catalog/tests/commonCatalog.test.tsx', 'Table_shows_sources_unlinked_state_and_pages_and_opens_links_of_the_product')],
    },
  },
  {
    id: 'platform.catalog.open',
    windows: [W.nomenclature],
    label: 'Общий товар',
    summary:
      'Нажатие на наименование открывает окно общего товара: там его можно посмотреть и править; нажатие на фото — фото на весь экран. Закрыли окно — поиск и страница те же.',
    effect: 'view',
    evidence: {
      unit: [pkg('catalog/tests/commonCatalog.test.tsx', 'Product_photo_opens_full_screen_and_the_name_opens_the_common_product')],
    },
  },
  {
    id: 'platform.catalog.create',
    windows: [W.nomenclature],
    label: 'Создать товар',
    summary: 'Открывает окно нового общего товара. Товар появится в каталоге только после «Создать товар» в этом окне.',
    effect: 'view',
    evidence: { unit: [pkg('catalog/tests/commonCatalog.test.tsx', 'Create_button_opens_the_new_common_product_window')] },
  },
]

const VAT_UI = 'vat-settings/tests/VatSettings.test.tsx'
const VAT_SRV = 'tests/Resource.OneC/Resource.OneC.Tests/Catalog/Products/ProductVatTests.cs'

/** Вкладка «Настройка» номенклатуры: ставка НДС товаров по источникам и её массовая установка. */
const NOMENCLATURE_SETTINGS: readonly ActionDescriptor[] = [
  {
    id: 'platform.nomenclature-settings.reload',
    windows: [W.nomenclatureSettings],
    label: 'Обновить',
    summary: 'Заново считает ставки НДС у товаров во всех источниках и в общем каталоге. Ничего не меняет.',
    effect: 'read-external',
    effectText: 'Читает номенклатуру баз 1С и сводку кабинетов; записи нет.',
    evidence: { unit: [pkg(VAT_UI, 'Summary_shows_counts_and_says_no_data_instead_of_zeros')] },
  },
  {
    id: 'platform.nomenclature-settings.source-reload',
    windows: [W.nomenclatureSettings],
    label: 'Обновить сводку источника',
    summary: 'Заново считает ставки НДС у товаров одного источника. Ничего не меняет.',
    effect: 'read-external',
    effectText: 'Читает номенклатуру базы 1С или сводку кабинета; записи нет.',
    disabledWhen: 'Выключена, пока идёт подсчёт.',
    evidence: {
      unit: [pkg(VAT_UI, 'Summary_shows_counts_and_says_no_data_instead_of_zeros')],
      server: [unit(VAT_SRV, 'Summary_counts_products_with_and_without_a_rate_and_ignores_groups_and_deleted_products')],
    },
  },
  {
    id: 'platform.nomenclature-settings.rate',
    windows: [W.nomenclatureSettings],
    label: 'Ставка',
    summary: 'Какую ставку НДС задать товарам. Список берётся из справочника ставок самой базы.',
    effect: 'view',
    evidence: { unit: [pkg(VAT_UI, 'Preview_shows_counts_and_the_apply_button_waits_for_the_typed_number')] },
  },
  {
    id: 'platform.nomenclature-settings.group',
    windows: [W.nomenclatureSettings],
    label: 'Какие товары',
    summary: 'Все товары источника или только выбранная группа вместе с вложенными. Рядом с группой видно, сколько в ней товаров и сколько без ставки.',
    effect: 'view',
    evidence: { unit: [pkg(VAT_UI, 'Preview_shows_counts_and_the_apply_button_waits_for_the_typed_number')] },
  },
  {
    id: 'platform.nomenclature-settings.preview',
    windows: [W.nomenclatureSettings],
    label: 'Показать, что изменится',
    summary: 'Считает, у скольких товаров ставка изменится, у скольких она уже такая и сколько будет пропущено. Ничего не записывает.',
    effect: 'read-external',
    effectText: 'Читает всю номенклатуру базы 1С; записи нет.',
    access: 'Тот, кому выдано право изменять каталог в этой базе.',
    disabledWhen: 'Выключена, пока не выбрана ставка.',
    evidence: {
      unit: [pkg(VAT_UI, 'Preview_shows_counts_and_the_apply_button_waits_for_the_typed_number')],
      server: [unit(VAT_SRV, 'Preview_reports_what_will_change_what_is_already_set_and_what_is_skipped_without_writing')],
    },
  },
  {
    id: 'platform.nomenclature-settings.apply',
    windows: [W.nomenclatureSettings],
    label: 'Записать',
    summary:
      'Записывает выбранную ставку в карточки товаров, которые показал предпросмотр. Идёт пачками по 50; каждая правка проверяется чтением. Остановить на середине нельзя — уже записанное не откатывается автоматически.',
    effect: 'write-external',
    effectText: 'Меняет карточки номенклатуры в учётной системе (1С).',
    access: 'Тот, кому выдано право изменять каталог в этой базе.',
    disabledWhen: 'Выключена, пока не набрано число изменяемых товаров из предпросмотра.',
    evidence: {
      unit: [pkg(VAT_UI, 'Preview_shows_counts_and_the_apply_button_waits_for_the_typed_number')],
      server: [
        unit(VAT_SRV, 'Apply_writes_the_rate_reports_every_product_and_a_repeat_of_the_same_command_does_not_write_again'),
        unit(VAT_SRV, 'Apply_reports_a_partial_result_and_never_resends_a_post_after_a_refusal'),
      ],
    },
  },
  {
    id: 'platform.nomenclature-settings.cancel',
    windows: [W.nomenclatureSettings],
    label: 'Отмена',
    summary: 'Возвращает к выбору ставки и товаров. Ничего не записано.',
    effect: 'view',
    evidence: { unit: [pkg(VAT_UI, 'Preview_shows_counts_and_the_apply_button_waits_for_the_typed_number')] },
  },
  {
    id: 'platform.nomenclature-settings.close',
    windows: [W.nomenclatureSettings],
    label: 'Закрыть отчёт',
    summary: 'Закрывает отчёт о записи. Записанное остаётся; сводка источника уже обновлена.',
    effect: 'view',
    evidence: { unit: [pkg(VAT_UI, 'The_report_lists_every_problem_product_and_is_honest_about_a_partial_result')] },
  },
]

/** Вход раздела «РМК» (UI-RMK-FLOW 3.13): плитки источников, где человек может работать за кассой. */
const RMK_SOURCES: readonly ActionDescriptor[] = [
  {
    id: 'platform.rmk.reload',
    windows: [W.rmk],
    label: 'Обновить',
    summary: 'Заново узнаёт, где вы можете работать за кассой. Кассу при этом никто не опрашивает.',
    effect: 'read',
    evidence: {
      unit: [pkg('rmk-sources/tests/RmkSourcesScreen.test.tsx', 'Failed_list_shows_service_text_and_retry')],
    },
  },
  {
    id: 'platform.rmk.open-source',
    windows: [W.rmk],
    label: 'Открыть',
    summary: 'Открывает смены и кассы этого источника. Ничего не отправляет и не меняет.',
    effect: 'view',
    evidence: {
      unit: [
        pkg('rmk-sources/tests/RmkSourcesScreen.test.tsx', 'Source_tile_shows_mark_noun_name_and_one_open_button_and_is_silent_about_working_contact'),
        web('rmkSources.test.tsx', 'Rmk_section_shows_a_tile_for_each_cash_source_even_when_there_is_one'),
      ],
    },
  },
  {
    id: 'platform.rmk.sources',
    windows: [W.rmk],
    label: 'Источники',
    summary: 'Открывает список подключённых баз и кабинетов, где администратор подключает кассу.',
    effect: 'view',
    access: ADMIN,
    evidence: {
      unit: [pkg('rmk-sources/tests/RmkSourcesScreen.test.tsx', 'Empty_list_asks_cashier_to_call_administrator_and_offers_sources_to_administrator')],
    },
  },
]

/** Вход раздела «Чеки»: тот же выбор источника плитками, что и в «РМК» (универсальный `ResourcePickerScreen`). */
const RECEIPTS_SOURCES: readonly ActionDescriptor[] = [
  {
    id: 'platform.receipts.reload',
    windows: [W.receipts],
    label: 'Обновить',
    summary: 'Заново узнаёт, чьи чеки вам можно смотреть. Сами источники при этом никто не опрашивает.',
    effect: 'read',
    evidence: {
      unit: [web('receiptsSources.test.tsx', 'Failed_list_of_receipt_sources_shows_service_text_and_retry')],
    },
  },
  {
    id: 'platform.receipts.open-source',
    windows: [W.receipts],
    label: 'Открыть',
    summary: 'Открывает чеки этого источника: дерево организаций, магазинов и касс, дни недели и итоги. Ничего не отправляет и не меняет.',
    effect: 'view',
    evidence: {
      unit: [web('receiptsSources.test.tsx', 'Receipts_section_shows_a_tile_for_each_source_and_opens_nothing_by_itself')],
    },
  },
  {
    id: 'platform.receipts.sources',
    windows: [W.receipts],
    label: 'Источники',
    summary: 'Открывает список подключённых баз и кабинетов, где администратор подключает источник чеков.',
    effect: 'view',
    access: ADMIN,
    evidence: {
      unit: [web('receiptsSources.test.tsx', 'Receipts_section_without_sources_offers_sources_to_the_administrator')],
    },
  },
]

// «Цены» начинаются с выбора источника плитками (как «Чеки»): внутри источника — его собственное управление ценами.
const PRICES_SOURCES: readonly ActionDescriptor[] = [
  {
    id: 'platform.prices.reload',
    windows: [W.prices],
    label: 'Обновить',
    summary: 'Заново узнаёт, чьими ценами вам можно управлять. Сами источники при этом никто не опрашивает.',
    effect: 'read',
    evidence: {
      unit: [web('pricesSources.test.tsx', 'Failed_list_of_price_sources_shows_service_text_and_retry')],
    },
  },
  {
    id: 'platform.prices.open-source',
    windows: [W.prices],
    label: 'Открыть',
    summary: 'Открывает цены этого источника: у 1С — прайс по видам цен, у кабинета AQSI — товары с ценой. Ничего не отправляет и не меняет.',
    effect: 'view',
    evidence: {
      unit: [web('pricesSources.test.tsx', 'Prices_section_shows_a_tile_for_each_source_and_opens_nothing_by_itself')],
    },
  },
  {
    id: 'platform.prices.sources',
    windows: [W.prices],
    label: 'Источники',
    summary: 'Открывает список подключённых баз и кабинетов, где администратор подключает источник цен.',
    effect: 'view',
    access: ADMIN,
    evidence: {
      unit: [web('pricesSources.test.tsx', 'Prices_section_without_sources_offers_sources_to_the_administrator')],
    },
  },
]

// Остальные разделы с выбором источника плитками (замечание владельца 17.10.2026: везде один вид выбора источника).
const SOURCE_PICKERS: readonly { id: string; window: string; section: string; what: string; opens: string; slug: string }[] = [
  { id: 'supplies', window: W.supplies, section: 'Поставки', what: 'чьими поставками вам можно заниматься', opens: 'поставки этого источника', slug: 'Supplies' },
  { id: 'inventory', window: W.inventory, section: 'Инвентаризация', what: 'чью инвентаризацию вам можно вести', opens: 'инвентаризацию этого источника', slug: 'Inventory' },
  { id: 'price-tags', window: W.priceTags, section: 'Ценники', what: 'чьи ценники вам можно печатать', opens: 'ценники этого источника', slug: 'PriceTags' },
  { id: 'import', window: W.import, section: 'Импорт накладных', what: 'в какие базы вам можно загружать накладные', opens: 'импорт накладных в эту базу', slug: 'Import' },
  { id: 'structure', window: W.structure, section: 'Структура', what: 'чью структуру вам можно смотреть', opens: 'структуру этого источника', slug: 'Structure' },
  { id: 'sales-statistics', window: W.salesStatistics, section: 'Статистика по продажам', what: 'чью статистику продаж вам можно смотреть', opens: 'статистику продаж этого источника', slug: 'SalesStatistics' },
  { id: 'shifts', window: W.shifts, section: 'Смены', what: 'чьи смены вам можно смотреть', opens: 'смены этого источника', slug: 'Shifts' },
  // Закладка `/aqsi/catalog` (RP-SRC1, Е-3): выбор источника каталога — те же плитки, окно принадлежит пакету AQSI.
  { id: 'catalog-source', window: 'aqsi.catalog-source', section: 'Номенклатура', what: 'чей каталог вам можно смотреть', opens: 'каталог этого источника', slug: 'CatalogSource' },
  // Раздел «Дубли» (RP-DUP8): окно входа принадлежит пакету дублей, плитки — те же.
  { id: 'duplicates-source', window: 'duplicates.home', section: 'Дубли', what: 'в каких источниках вам можно искать дубли', opens: 'дубли этого источника', slug: 'Duplicates' },
]

const SECTION_SOURCES: readonly ActionDescriptor[] = SOURCE_PICKERS.flatMap((picker): ActionDescriptor[] => [
  {
    id: `platform.${picker.id}.reload`,
    windows: [picker.window],
    label: 'Обновить',
    summary: `Заново узнаёт, ${picker.what}. Сами источники при этом никто не опрашивает.`,
    effect: 'read',
    evidence: { unit: [web('sectionPickers.test.tsx', `Failed_list_of_${picker.slug}_sources_shows_service_text_and_retry`)] },
  },
  {
    id: `platform.${picker.id}.open-source`,
    windows: [picker.window],
    label: 'Открыть',
    summary: `Открывает ${picker.opens}. Ничего не отправляет и не меняет.`,
    effect: 'view',
    evidence: { unit: [web('sectionPickers.test.tsx', `${picker.slug}_section_shows_a_tile_for_each_source_and_opens_nothing_by_itself`)] },
  },
  {
    id: `platform.${picker.id}.sources`,
    windows: [picker.window],
    label: 'Источники',
    summary: `Открывает список подключённых баз и кабинетов, где администратор подключает источник раздела «${picker.section}».`,
    effect: 'view',
    access: ADMIN,
    evidence: { unit: [web('sectionPickers.test.tsx', `${picker.slug}_section_without_sources_offers_sources_to_the_administrator`)] },
  },
])

const TESTER = 'Администратор и тот, кому выдано право на замеры поиска.'
const TESTING_FILE = 'searchTesting.test.tsx'
const menuGate = web(TESTING_FILE, 'Testing_menu_item_and_window_need_the_testing_right')
const productsFlow = web(TESTING_FILE, 'Test_product_is_created_copied_from_the_catalog_edited_and_removed')
const photoFlow = web(TESTING_FILE, 'Frames_are_added_with_tags_and_checked_lane_by_lane_and_after_fusion')
const editFlow = web(TESTING_FILE, 'Test_product_edit_carries_the_seen_version')
const runsFlow = web(TESTING_FILE, 'Run_scores_always_carry_n_and_an_interval_and_products_go_worst_first')
const testingServer = (name: string) => srv('Search/SearchTestingTests.cs', name)
const TEXT_TESTING_FILE = 'textSearchTesting.test.tsx'
const textHome = web(TEXT_TESTING_FILE, 'Text_search_opens_from_the_testing_tab_as_pages_with_back')
const textQueriesFlow = web(TEXT_TESTING_FILE, 'Queries_are_generated_added_filtered_checked_and_removed')
const textCheckFlow = web(TEXT_TESTING_FILE, 'Check_shows_the_step_the_hits_and_how_the_expected_product_matched')
const textRunsFlow = web(TEXT_TESTING_FILE, 'Text_run_shows_scores_by_kind_and_match_and_worst_queries_first')
const textProductsFlow = web(TEXT_TESTING_FILE, 'Text_search_has_its_own_test_products_page_without_photos')
const textServer = (name: string) => srv('Search/TextSearchTestingTests.cs', name)
const TWIN_FILE = 'twinTesting.test.tsx'
const twinHome = web(TWIN_FILE, 'Twin_variants_open_from_the_testing_tab_and_need_the_testing_right')
const twinFamiliesFlow = web(TWIN_FILE, 'Families_are_listed_by_priority_and_a_frame_is_added_to_a_variant')
const twinSeriesFlow = web(TWIN_FILE, 'A_series_is_shot_variant_by_variant_in_order')
const twinRunFlow = web(TWIN_FILE, 'Twin_run_is_started_shown_with_its_scores_and_exported')
const twinServer = (name: string) => srv('Search/TwinTestingTests.cs', name)

/** RP-FB29: вкладка «Тестирование» — тестовые товары и замеры поиска по фото. Боевой каталог и 1С не трогаются. */
const TESTING: readonly ActionDescriptor[] = [
  {
    id: 'platform.testing.open-photo-search',
    windows: [W.testing],
    label: 'Открыть',
    summary: 'Открывает проверку поиска по фото: тестовые товары, проверку кадра и замеры.',
    effect: 'view',
    access: TESTER,
    evidence: { unit: [menuGate] },
  },
  {
    id: 'platform.testing-products.reload',
    windows: [W.testingProducts, W.testingTextProducts],
    label: 'Обновить',
    summary: 'Перечитывает список тестовых товаров.',
    effect: 'read',
    access: TESTER,
    evidence: { unit: [productsFlow] },
  },
  {
    id: 'platform.testing-products.open-runs',
    windows: [W.testingProducts, W.testingTextProducts],
    label: 'Замеры',
    summary: 'Открывает прогоны замера: последний балл, история и запуск нового.',
    effect: 'view',
    access: TESTER,
    evidence: { unit: [runsFlow] },
  },
  {
    id: 'platform.testing-products.manual',
    windows: [W.testingProducts, W.testingTextProducts],
    label: 'Завести вручную',
    summary: 'Раскрывает форму нового тестового товара без товара каталога: название, артикул, штрихкоды. Фото такого товара добавляются вручную.',
    effect: 'view',
    access: TESTER,
    evidence: { unit: [productsFlow] },
  },
  {
    id: 'platform.testing-products.create',
    windows: [W.testingProducts, W.testingTextProducts],
    label: 'Создать',
    summary: 'Заводит тестовый товар с названием, артикулом и штрихкодами. Боевой каталог и учётные системы не меняются.',
    effect: 'write-local',
    effectText: 'Записывает товар в тестовый каталог нашего сервера.',
    access: TESTER,
    disabledWhen: 'Пока не введено название.',
    evidence: { unit: [productsFlow], server: [testingServer('Test_product_is_created_edited_and_deleted_together_with_its_points')] },
  },
  {
    id: 'platform.testing-products.search-catalog',
    windows: [W.testingProducts, W.testingTextProducts],
    label: 'Найти',
    summary: 'Ищет товар общего каталога по названию, артикулу или штрихкоду, чтобы сделать из него тестовый.',
    effect: 'read',
    access: TESTER,
    evidence: { unit: [productsFlow] },
  },
  {
    id: 'platform.testing-products.copy-from-catalog',
    windows: [W.testingProducts, W.testingTextProducts],
    label: 'Взять в тест',
    summary: 'Делает тестовый товар копией товара каталога: название, артикул, штрихкоды и фото. Дальше копия правится сама, каталог не меняется.',
    effect: 'write-local',
    effectText: 'Записывает копию в тестовый каталог нашего сервера.',
    access: TESTER,
    evidence: { unit: [productsFlow], server: [testingServer('Copy_from_catalog_takes_fields_and_photos_and_editing_the_copy_leaves_the_catalog_alone')] },
  },
  {
    id: 'platform.testing-products.open',
    windows: [W.testingProducts, W.testingTextProducts],
    label: 'Открыть',
    summary: 'Открывает карточку тестового товара: поля, эталоны и кадры.',
    effect: 'view',
    access: TESTER,
    evidence: { unit: [productsFlow] },
  },
  {
    id: 'platform.testing-products.delete',
    windows: [W.testingProducts, W.testingTextProducts],
    label: 'Удалить',
    summary: 'Удаляет тестовый товар с его фото после подтверждения. Прошлые прогоны остаются.',
    effect: 'write-local',
    effectText: 'Удаляет товар из тестового каталога нашего сервера.',
    access: TESTER,
    evidence: { unit: [productsFlow] },
  },
  {
    id: 'platform.testing-product.save',
    windows: [W.testingProduct, W.testingTextProduct],
    label: 'Сохранить',
    summary: 'Записывает название, артикул и штрихкоды тестового товара. Если товар успели изменить, сохранение откажет, а не перезапишет.',
    effect: 'write-local',
    access: TESTER,
    disabledWhen: 'Пока не введено название или ничего не изменено.',
    evidence: { unit: [editFlow] },
  },
  {
    id: 'platform.testing-product.refresh',
    windows: [W.testingProduct, W.testingTextProduct],
    label: 'Обновить из источника',
    summary: 'Заново берёт поля и фото из товара каталога, с которого сделана копия. Добавленные вручную эталоны и кадры остаются.',
    effect: 'write-local',
    access: TESTER,
    evidence: { unit: [productsFlow], server: [testingServer('Copy_from_catalog_takes_fields_and_photos_and_editing_the_copy_leaves_the_catalog_alone')] },
  },
  {
    id: 'platform.testing-product.reindex',
    windows: [W.testingProduct, W.testingTextProduct],
    label: 'Переиндексировать',
    summary: 'Заново кладёт товар и его эталоны в тестовый индекс поиска — если прошлый раз не удался.',
    effect: 'write-local',
    access: TESTER,
    evidence: { unit: [productsFlow] },
  },
  {
    id: 'platform.testing-product.delete',
    windows: [W.testingProduct, W.testingTextProduct],
    label: 'Удалить товар',
    summary: 'Удаляет тестовый товар с его фото после подтверждения.',
    effect: 'write-local',
    access: TESTER,
    evidence: { unit: [productsFlow] },
  },
  {
    id: 'platform.testing-product.add-photo',
    windows: [W.testingProduct],
    label: 'Добавить эталон / Добавить кадр',
    summary: 'В блоке «Эталоны» добавляет фото товара в тестовый индекс; в блоке «Кадры» — снимок, которым ищут, с необязательными пометками: ракурс, свет, качество.',
    effect: 'write-local',
    access: TESTER,
    disabledWhen: 'Пока не выбран файл.',
    evidence: { unit: [photoFlow] },
  },
  {
    id: 'platform.testing-product.frame-tags',
    windows: [W.testingProduct],
    label: 'Пометки кадра',
    summary: 'Раскрывает необязательные пометки кадра: ракурс, качество, свет, расстояние, перекрытие, упаковка, штрихкод на кадре. По ним замер показывает отдельно, например, только кадры с бликом.',
    effect: 'view',
    access: TESTER,
    evidence: { unit: [photoFlow] },
  },
  {
    id: 'platform.testing-product.photo-file',
    windows: [W.testingProduct],
    label: 'Файл фото',
    summary: 'Выбирает снимок с устройства. Большой снимок уменьшается так же, как перед боевым поиском.',
    effect: 'view',
    access: TESTER,
    evidence: { unit: [photoFlow] },
  },
  {
    id: 'platform.testing-product.delete-photo',
    windows: [W.testingProduct],
    label: 'Удалить',
    summary: 'Удаляет эталон или кадр тестового товара.',
    effect: 'write-local',
    access: TESTER,
    evidence: { unit: [photoFlow] },
  },
  {
    id: 'platform.testing-product.check-frame',
    windows: [W.testingProduct],
    label: 'Проверить',
    summary: 'Ищет по этому кадру тем же путём, что боевой поиск, среди тестовых товаров и показывает место нужного товара.',
    effect: 'view',
    access: TESTER,
    evidence: { unit: [photoFlow] },
  },
  {
    id: 'platform.testing-check.recheck',
    windows: [W.testingCheck],
    label: 'Проверить снова',
    summary: 'Повторяет поиск по кадру: каждая дорожка отдельно и после слияния. Ничего не записывает.',
    effect: 'read',
    access: TESTER,
    evidence: { unit: [photoFlow], server: [testingServer('All_lanes_mode_ranks_the_same_candidates_exactly_as_production_search')] },
  },
  {
    id: 'platform.testing-runs.reload',
    windows: [W.testingRuns],
    label: 'Обновить',
    summary: 'Перечитывает прогоны замера.',
    effect: 'read',
    access: TESTER,
    evidence: { unit: [runsFlow] },
  },
  {
    id: 'platform.testing-runs.open-products',
    windows: [W.testingRuns],
    label: 'Тестовые товары',
    summary: 'Открывает список тестовых товаров.',
    effect: 'view',
    access: TESTER,
    evidence: { unit: [runsFlow] },
  },
  {
    id: 'platform.testing-runs.lanes',
    windows: [W.testingRuns],
    label: 'Что мерить',
    summary: 'Все дорожки и слияние — как боевой поиск и каждая часть отдельно; или одна дорожка: фото, текст или штрихкод.',
    effect: 'view',
    access: TESTER,
    evidence: { unit: [runsFlow] },
  },
  {
    id: 'platform.testing-runs.start',
    windows: [W.testingRuns],
    label: 'Запустить замер',
    summary: 'Прогоняет все кадры тестовых товаров через поиск — все дорожки и слияние или одну дорожку — и считает баллы.',
    effect: 'write-local',
    effectText: 'Записывает прогон и его итоги на нашем сервере.',
    access: TESTER,
    disabledWhen: 'Пока идёт прошлый замер.',
    evidence: { unit: [runsFlow], server: [testingServer('Run_measures_every_frame_in_the_background_with_metrics_stamp_and_comparison')] },
  },
  {
    id: 'platform.testing-runs.cancel',
    windows: [W.testingRuns],
    label: 'Отменить',
    summary: 'Останавливает идущий замер; уже проверенные кадры остаются в прогоне.',
    effect: 'write-local',
    access: TESTER,
    evidence: { unit: [runsFlow] },
  },
  {
    id: 'platform.testing-runs.open',
    windows: [W.testingRuns],
    label: 'Открыть',
    summary: 'Открывает разбор прогона.',
    effect: 'view',
    access: TESTER,
    evidence: { unit: [runsFlow] },
  },
  {
    id: 'platform.testing-run.reload',
    windows: [W.testingRun],
    label: 'Обновить',
    summary: 'Перечитывает прогон: ход, баллы и сравнение с прошлым.',
    effect: 'read',
    access: TESTER,
    evidence: { unit: [runsFlow] },
  },
  {
    id: 'platform.testing-run.cancel',
    windows: [W.testingRun],
    label: 'Отменить',
    summary: 'Останавливает этот замер; уже проверенные кадры остаются.',
    effect: 'write-local',
    access: TESTER,
    evidence: { unit: [runsFlow] },
  },
  {
    id: 'platform.testing-run.open-previous',
    windows: [W.testingRun],
    label: 'Открыть прошлый',
    summary: 'Открывает прогон, с которым сравнивается этот.',
    effect: 'view',
    access: TESTER,
    evidence: { unit: [runsFlow] },
  },
  {
    id: 'platform.testing-run.open-product',
    windows: [W.testingRun],
    label: 'Открыть товар',
    summary: 'Открывает тестовый товар из разбора — посмотреть его эталоны и кадры.',
    effect: 'view',
    access: TESTER,
    evidence: { unit: [runsFlow] },
  },
  {
    id: 'platform.testing.open-text-search',
    windows: [W.testing],
    label: 'Открыть',
    summary: 'Открывает проверку поиска по тексту: запросы к тестовым товарам, проверку запроса и замеры.',
    effect: 'view',
    access: TESTER,
    evidence: { unit: [textHome], server: [textServer('Text_testing_needs_the_testing_right')] },
  },
  {
    id: 'platform.testing-text-queries.reload',
    windows: [W.testingTextQueries],
    label: 'Обновить',
    summary: 'Перечитывает запросы.',
    effect: 'read',
    access: TESTER,
    evidence: { unit: [textQueriesFlow] },
  },
  {
    id: 'platform.testing-text-queries.open-check',
    windows: [W.testingTextQueries],
    label: 'Проверить запрос',
    summary: 'Открывает проверку любого запроса: что найдёт поиск номенклатуры и на какой ступени.',
    effect: 'view',
    access: TESTER,
    evidence: { unit: [textHome] },
  },
  {
    id: 'platform.testing-text-queries.open-runs',
    windows: [W.testingTextQueries],
    label: 'Замеры',
    summary: 'Открывает замеры поиска по тексту.',
    effect: 'view',
    access: TESTER,
    evidence: { unit: [textHome] },
  },
  {
    id: 'platform.testing-text-queries.open-products',
    windows: [W.testingTextQueries],
    label: 'Товары',
    summary: 'Открывает тестовые товары без фото: добавить товар из каталога, поправить название, артикул и штрихкоды, удалить. Товары общие с поиском по фото.',
    effect: 'view',
    access: TESTER,
    evidence: { unit: [textProductsFlow] },
  },
  {
    id: 'platform.testing-text-queries.generate',
    windows: [W.testingTextQueries],
    label: 'Сгенерировать',
    summary: 'Делает запросы из каждого тестового товара: название, часть названия, опечатка, раскладка, артикул и штрихкоды. Прежние сгенерированные заменяются, свои остаются.',
    effect: 'write-local',
    effectText: 'Записывает на нашем сервере; боевой каталог, 1С и AQSI не трогаются.',
    disabledWhen: 'Пока нет ни одного тестового товара.',
    access: TESTER,
    evidence: { unit: [textQueriesFlow], server: [textServer('Generation_replaces_generated_queries_and_keeps_the_ones_a_person_added'), textServer('Generator_makes_every_kind_of_query_the_way_a_seller_types')] },
  },
  {
    id: 'platform.testing-text-queries.product',
    windows: [W.testingTextQueries],
    label: 'Должен найтись',
    summary: 'Тестовый товар, который запрос должен найти.',
    effect: 'view',
    access: TESTER,
    evidence: { unit: [textQueriesFlow] },
  },
  {
    id: 'platform.testing-text-queries.kind',
    windows: [W.testingTextQueries],
    label: 'Вид',
    summary: 'Что проверяет запрос: название, часть, опечатку, раскладку, артикул, штрихкод, синоним или другое — по виду считаются срезы замера.',
    effect: 'view',
    access: TESTER,
    evidence: { unit: [textQueriesFlow] },
  },
  {
    id: 'platform.testing-text-queries.add',
    windows: [W.testingTextQueries],
    label: 'Добавить запрос',
    summary: 'Сохраняет свой запрос; при новой генерации он остаётся.',
    effect: 'write-local',
    effectText: 'Записывает на нашем сервере; боевой каталог, 1С и AQSI не трогаются.',
    disabledWhen: 'Пока запрос пуст или нет тестовых товаров.',
    access: TESTER,
    evidence: { unit: [textQueriesFlow] },
  },
  {
    id: 'platform.testing-text-queries.filter',
    windows: [W.testingTextQueries],
    label: 'Вид запроса',
    summary: 'Показывает запросы одного вида.',
    effect: 'view',
    access: TESTER,
    evidence: { unit: [textQueriesFlow] },
  },
  {
    id: 'platform.testing-text-queries.check',
    windows: [W.testingTextQueries],
    label: 'Проверить',
    summary: 'Открывает проверку этого запроса с его товаром.',
    effect: 'view',
    access: TESTER,
    evidence: { unit: [textQueriesFlow] },
  },
  {
    id: 'platform.testing-text-queries.delete',
    windows: [W.testingTextQueries],
    label: 'Удалить',
    summary: 'Удаляет запрос после подтверждения; прошлые замеры остаются.',
    effect: 'write-local',
    effectText: 'Записывает на нашем сервере; боевой каталог, 1С и AQSI не трогаются.',
    access: TESTER,
    evidence: { unit: [textQueriesFlow] },
  },
  {
    id: 'platform.testing-text-check.open-queries',
    windows: [W.testingTextCheck, W.testingTextQueryCheck],
    label: 'Запросы',
    summary: 'Возвращает к списку запросов.',
    effect: 'view',
    access: TESTER,
    evidence: { unit: [textCheckFlow] },
  },
  {
    id: 'platform.testing-text-check.product',
    windows: [W.testingTextCheck, W.testingTextQueryCheck],
    label: 'Должен найтись',
    summary: 'Тестовый товар, место которого показать в выдаче; можно не задавать.',
    effect: 'view',
    access: TESTER,
    evidence: { unit: [textCheckFlow] },
  },
  {
    id: 'platform.testing-text-check.check',
    windows: [W.testingTextCheck, W.testingTextQueryCheck],
    label: 'Проверить',
    summary: 'Ищет запрос тем же путём, что номенклатура: штрихкод и артикул целиком из базы, длинные цифры без совпадения — пусто, остальное — служба text-search. Ничего не записывает.',
    effect: 'read',
    access: TESTER,
    evidence: { unit: [textCheckFlow], server: [textServer('Check_goes_the_nomenclature_way_codes_from_the_database_long_digits_empty_the_rest_to_the_service')] },
  },
  {
    id: 'platform.testing-text-check.kind',
    windows: [W.testingTextCheck, W.testingTextQueryCheck],
    label: 'Вид запроса',
    summary: 'Вид, с которым запрос сохранится.',
    effect: 'view',
    access: TESTER,
    evidence: { unit: [textCheckFlow] },
  },
  {
    id: 'platform.testing-text-check.save',
    windows: [W.testingTextCheck, W.testingTextQueryCheck],
    label: 'Сохранить как запрос',
    summary: 'Сохраняет проверенный запрос с его товаром в список запросов.',
    effect: 'write-local',
    effectText: 'Записывает на нашем сервере; боевой каталог, 1С и AQSI не трогаются.',
    access: TESTER,
    evidence: { unit: [textCheckFlow] },
  },
  {
    id: 'platform.testing-text-runs.open-queries',
    windows: [W.testingTextRuns],
    label: 'Запросы',
    summary: 'Открывает список запросов.',
    effect: 'view',
    access: TESTER,
    evidence: { unit: [textRunsFlow] },
  },
  {
    id: 'platform.testing-text-runs.reload',
    windows: [W.testingTextRuns],
    label: 'Обновить',
    summary: 'Перечитывает замеры.',
    effect: 'read',
    access: TESTER,
    evidence: { unit: [textRunsFlow] },
  },
  {
    id: 'platform.testing-text-runs.start',
    windows: [W.testingTextRuns],
    label: 'Запустить замер',
    summary: 'Прогоняет все запросы через поиск номенклатуры в фоне и считает баллы по видам запросов и по тому, чем нашёлся товар.',
    effect: 'write-local',
    effectText: 'Записывает замер и его итоги на нашем сервере.',
    disabledWhen: 'Пока идёт прошлый замер — по фото или по тексту.',
    access: TESTER,
    evidence: { unit: [textRunsFlow], server: [textServer('Run_measures_every_query_in_the_background_by_kind_and_match_apart_from_photo_runs')] },
  },
  {
    id: 'platform.testing-text-runs.cancel',
    windows: [W.testingTextRuns],
    label: 'Отменить',
    summary: 'Останавливает идущий замер; уже проверенные запросы остаются в нём.',
    effect: 'write-local',
    access: TESTER,
    evidence: { unit: [textRunsFlow] },
  },
  {
    id: 'platform.testing-text-runs.open',
    windows: [W.testingTextRuns],
    label: 'Открыть',
    summary: 'Открывает разбор замера.',
    effect: 'view',
    access: TESTER,
    evidence: { unit: [textRunsFlow] },
  },
  {
    id: 'platform.testing-text-run.reload',
    windows: [W.testingTextRun],
    label: 'Обновить',
    summary: 'Перечитывает замер: ход, баллы и сравнение с прошлым.',
    effect: 'read',
    access: TESTER,
    evidence: { unit: [textRunsFlow] },
  },
  {
    id: 'platform.testing-text-run.cancel',
    windows: [W.testingTextRun],
    label: 'Отменить',
    summary: 'Останавливает идущий замер; уже проверенные запросы остаются в нём.',
    effect: 'write-local',
    access: TESTER,
    evidence: { unit: [textRunsFlow] },
  },
  {
    id: 'platform.testing-text-run.open-previous',
    windows: [W.testingTextRun],
    label: 'Открыть прошлый',
    summary: 'Открывает прошлый замер, с которым идёт сравнение.',
    effect: 'view',
    access: TESTER,
    evidence: { unit: [textRunsFlow] },
  },
  {
    id: 'platform.testing-text-run.check',
    windows: [W.testingTextRun],
    label: 'Проверить',
    summary: 'Открывает проверку этого запроса — посмотреть выдачу и кто обогнал нужный товар.',
    effect: 'view',
    access: TESTER,
    evidence: { unit: [textRunsFlow] },
  },
  // --- RP-FB67: «Похожие варианты» -----------------------------------------------------------------------------------------
  {
    id: 'platform.testing.open-twin',
    windows: [W.testing],
    label: 'Открыть',
    summary: 'Открывает проверку похожих вариантов: семейства товаров одной серии из каталога, кадры вариантов и замер, не путает ли поиск варианты.',
    effect: 'view',
    access: TESTER,
    evidence: { unit: [twinHome], server: [twinServer('Twin_endpoints_need_the_testing_right_and_refuse_a_run_without_twin_frames')] },
  },
  {
    id: 'platform.testing-twin.open-products',
    windows: [W.testingTwin],
    label: 'Тестовые товары',
    summary: 'Открывает список тестовых товаров: варианты, которым снят кадр, уже лежат там.',
    effect: 'view',
    access: TESTER,
    evidence: { unit: [twinHome] },
  },
  {
    id: 'platform.testing-twin.reload',
    windows: [W.testingTwin],
    label: 'Обновить',
    summary: 'Перечитывает семейства и замеры с сервера.',
    effect: 'read',
    access: TESTER,
    evidence: { unit: [twinFamiliesFlow] },
  },
  {
    id: 'platform.testing-twin.filter',
    windows: [W.testingTwin],
    label: 'Отбор семейств',
    summary: 'Показывает семейства одного вида: где фото есть не у всех вариантов (им нужны фото), у всех или ни у кого.',
    effect: 'view',
    access: TESTER,
    evidence: { unit: [twinFamiliesFlow] },
  },
  {
    id: 'platform.testing-twin.recompute',
    windows: [W.testingTwin],
    label: 'Пересчитать по каталогу',
    summary: 'Заново ищет семейства по названиям всех товаров каталога. Нужно после того, как в каталоге появились новые товары; на большом каталоге занимает несколько секунд.',
    effect: 'read',
    access: TESTER,
    evidence: { unit: [twinFamiliesFlow] },
  },
  {
    id: 'platform.testing-twin.shoot-variant',
    windows: [W.testingTwin],
    label: 'Добавить кадр',
    summary: 'Начинает съёмку кадра для этого варианта: камера или файл. Кадр попадёт в тестовый товар варианта, а сам товар заведётся из каталога, если его ещё нет.',
    effect: 'view',
    access: TESTER,
    disabledWhen: 'Пока не закончена съёмка другого варианта.',
    evidence: { unit: [twinFamiliesFlow] },
  },
  {
    id: 'platform.testing-twin.shoot-series',
    windows: [W.testingTwin],
    label: 'Снять серию по порядку',
    summary: 'Ведёт по вариантам серии один за другим: «1/2», «2/2» — снимите или выберите кадр каждого варианта.',
    effect: 'view',
    access: TESTER,
    disabledWhen: 'Пока не закончена съёмка другого варианта.',
    evidence: { unit: [twinSeriesFlow] },
  },
  {
    id: 'platform.testing-twin.pick-photo',
    windows: [W.testingTwin],
    label: 'Выбрать или снять фото',
    summary: 'Открывает лист выбора источника: камера телефона или файл на устройстве. Сам ничего не отправляет.',
    effect: 'view',
    access: TESTER,
    evidence: { unit: [twinFamiliesFlow] },
  },
  {
    id: 'platform.testing-twin.add-frame',
    windows: [W.testingTwin],
    label: 'Добавить кадр',
    summary: 'Отправляет выбранный снимок как кадр этого варианта и ставит товару метку серии. Большой снимок сначала уменьшается так же, как перед боевым поиском; тот же файл второй раз не заводится.',
    effect: 'write-local',
    effectText: 'Записывает тестовый товар (если его не было) и кадр на нашем сервере; боевой каталог, 1С и AQSI не трогаются.',
    access: TESTER,
    disabledWhen: 'Пока снимок отправляется.',
    evidence: {
      unit: [twinFamiliesFlow, twinSeriesFlow],
      server: [twinServer('A_twin_frame_made_from_a_catalog_product_creates_the_variant_once_and_sets_its_series')],
    },
  },
  {
    id: 'platform.testing-twin.shoot-cancel',
    windows: [W.testingTwin],
    label: 'Закончить съёмку',
    summary: 'Прекращает съёмку: уже отправленные кадры остаются, неотправленный снимок отбрасывается.',
    effect: 'view',
    access: TESTER,
    evidence: { unit: [twinSeriesFlow] },
  },
  {
    id: 'platform.testing-twin.start',
    windows: [W.testingTwin],
    label: 'Запустить замер двойников',
    summary: 'Прогоняет кадры вариантов серий через поиск и считает, как часто он выдаёт двойника вместо нужного товара. Замер идёт в фоне.',
    effect: 'write-local',
    effectText: 'Записывает замер и его итоги на нашем сервере.',
    access: TESTER,
    disabledWhen: 'Пока идёт другой замер: очередь замеров общая.',
    evidence: { unit: [twinRunFlow], server: [twinServer('Run_measures_twin_frames_in_the_background_and_exports_the_evidence_of_every_candidate')] },
  },
  {
    id: 'platform.testing-twin.start-all',
    windows: [W.testingTwin],
    label: 'Замер по всем кадрам (для калибровки)',
    summary: 'То же, но берёт все кадры тестовых товаров, а не только двойников: выгрузка для калибровки получает и кадры без двойников. Показатели двойников считаются, как и раньше, только по кадрам двойников.',
    effect: 'write-local',
    effectText: 'Записывает замер и его итоги на нашем сервере.',
    access: TESTER,
    disabledWhen: 'Пока идёт другой замер: очередь замеров общая.',
    evidence: { unit: [twinRunFlow] },
  },
  {
    id: 'platform.testing-twin.export-csv',
    windows: [W.testingTwin],
    label: 'Выгрузка для калибровки (таблица)',
    summary: 'Скачивает таблицу по последнему готовому замеру: по каждому кадру и каждому кандидату — сходство по фото, свидетельство текста, штрихкод, числа с этикетки. Картинок в ней нет. Из неё подбирают таблицу калибровки.',
    effect: 'read',
    access: TESTER,
    disabledWhen: 'Пока нет ни одного готового замера.',
    evidence: { unit: [twinRunFlow], server: [twinServer('Run_measures_twin_frames_in_the_background_and_exports_the_evidence_of_every_candidate')] },
  },
  {
    id: 'platform.testing-twin.export-json',
    windows: [W.testingTwin],
    label: 'Выгрузка для калибровки (JSON)',
    summary: 'Открывает те же данные, что в таблице, одним документом: для разбора программой.',
    effect: 'read',
    access: TESTER,
    disabledWhen: 'Пока нет ни одного готового замера.',
    evidence: { unit: [twinRunFlow], server: [twinServer('Run_measures_twin_frames_in_the_background_and_exports_the_evidence_of_every_candidate')] },
  },
  {
    id: 'platform.testing-twin.open-run',
    windows: [W.testingTwin],
    label: 'Разобрать',
    summary: 'Показывает разбор замера ниже: итог, серии, кого с кем путает, условия.',
    effect: 'view',
    access: TESTER,
    evidence: { unit: [twinRunFlow] },
  },
  {
    id: 'platform.testing-twin.cancel',
    windows: [W.testingTwin],
    label: 'Отменить',
    summary: 'Останавливает идущий замер; уже проверенные кадры остаются в нём.',
    effect: 'write-local',
    access: TESTER,
    evidence: { unit: [twinRunFlow] },
  },
  {
    id: 'platform.testing-twin.open-previous',
    windows: [W.testingTwin],
    label: 'Разобрать прошлый',
    summary: 'Показывает разбор замера, с которым сравнивается этот.',
    effect: 'view',
    access: TESTER,
    evidence: { unit: [twinRunFlow] },
  },
]

const CP_UI = 'common-product/tests/commonProduct.test.tsx'
const CP_SRV = 'Catalog/CommonCatalogEditTests.cs'
const CP_WRITE = 'Тот, кому выдано право менять общий каталог товаров.'
const CP_SAVE_UI = pkg(CP_UI, 'Edit_screen_saves_only_after_a_change_and_sends_the_version_it_showed')
const CP_DISCARD_UI = pkg(CP_UI, 'Discard_asks_for_confirmation_and_then_returns_the_saved_values')
const CP_CONFLICT_UI = pkg(CP_UI, 'Conflict_shows_was_now_mine_and_blocks_saving_until_the_person_decides')
const CP_GUARD_UI = pkg(CP_UI, 'Closing_the_window_with_unsaved_changes_asks_first_and_the_guard_is_removed_when_clean')
const CP_BARCODES_UI = pkg(CP_UI, 'Barcodes_are_a_list_with_add_and_remove_buttons_and_an_empty_name_blocks_saving')
const CP_ARCHIVE_UI = pkg(CP_UI, 'Archive_asks_for_confirmation_and_sends_the_version_it_showed')

/** Проверка обрезки фото человеком (RP-FB73): общая для создания товара из сканирования и «Прикрепить фото». */
const PCR_UI = 'common-photos/tests/photoCrop.test.tsx'
const PCR_ACCEPT_UI = pkg(PCR_UI, 'Review_shows_the_cutout_with_a_dashed_box_the_size_caption_and_accept_sends_the_proposed_box')
const PCR_DRAG_UI = pkg(PCR_UI, 'Dragging_a_corner_handle_changes_the_box_and_accept_sends_the_changed_box')
const PCR_FAIL_UI = pkg(PCR_UI, 'A_failed_save_keeps_the_review_with_the_server_text_and_the_box_and_accept_tries_again')
const PCR_CANCEL_UI = pkg(PCR_UI, 'Cancel_calls_the_cancel_handler_and_nothing_is_accepted')
const PCR_ZOOM_UI = pkg(PCR_UI, 'Zoom_buttons_always_exist_and_stop_at_the_limits_and_fit_returns_to_the_whole_photo')
const PCR_RESET_UI = pkg(PCR_UI, 'Reset_returns_the_box_to_the_proposed_one')

const SP_UI = 'source-product/tests/sourceProduct.test.tsx'

const COMMON_PRODUCT: readonly ActionDescriptor[] = [
  {
    id: 'platform.common-product.reload',
    windows: [W.commonProduct],
    label: 'Обновить',
    summary: 'Перечитывает товар из общего каталога. Несохранённые правки не пропадают: если товар изменили, поля будут сверены.',
    effect: 'read',
    access: CP_WRITE,
    evidence: {
      unit: [CP_CONFLICT_UI],
      server: [srv(CP_SRV, 'Created_product_is_readable_with_its_version_and_barcodes')],
    },
  },
  {
    id: 'platform.common-product.save',
    windows: [W.commonProduct, W.commonProductNew],
    label: 'Сохранить',
    summary:
      'Записывает изменённые поля в общий каталог, в том числе кассовые — ставку НДС, единицу, весовой признак, предмет и способ расчёта (у нового товара — создаёт его). Источники подтянут изменение по настройкам синхронизации: по умолчанию они выключены, и ничего никуда не уходит.',
    effect: 'write-local',
    effectText: 'Пишет в общий каталог и в журнал товара; в базы и кабинеты источников само нажатие ничего не отправляет.',
    access: CP_WRITE,
    disabledWhen: 'Выключена, пока нет изменений, пустое наименование или есть расхождения с чужой правкой.',
    evidence: {
      unit: [
        CP_SAVE_UI,
        pkg(CP_UI, 'Create_screen_has_no_sources_or_archive_and_sends_the_whole_form'),
        pkg('common-product/tests/cashFields.test.tsx', 'Choosing_a_vat_rate_enables_save_and_the_note_names_the_unknown_value_it_had_before'),
      ],
      server: [
        srv(CP_SRV, 'Patch_changes_only_the_given_fields_and_raises_the_version_and_writes_the_journal'),
        srv(CP_SRV, 'Patch_from_a_stale_version_is_a_conflict_and_changes_nothing'),
        srv(CP_SRV, 'Repeating_a_create_with_the_same_operation_id_returns_the_same_product_and_a_changed_body_is_a_conflict'),
      ],
    },
  },
  {
    id: 'platform.common-product.discard',
    windows: [W.commonProduct, W.commonProductNew],
    label: 'Отменить изменения',
    summary: 'Предлагает вернуть поля к сохранённым значениям. Само ничего не меняет: сначала спросит подтверждение.',
    effect: 'view',
    evidence: { unit: [CP_DISCARD_UI] },
  },
  {
    id: 'platform.common-product.discard-confirm',
    windows: [W.commonProduct, W.commonProductNew],
    label: 'Да, отменить',
    summary: 'Возвращает поля к сохранённым значениям; всё, что вы ввели и не сохранили, пропадает.',
    effect: 'view',
    evidence: { unit: [CP_DISCARD_UI] },
  },
  {
    id: 'platform.common-product.discard-keep',
    windows: [W.commonProduct, W.commonProductNew],
    label: 'Нет',
    summary: 'Закрывает вопрос и оставляет ваши правки как есть.',
    effect: 'view',
    evidence: { unit: [CP_DISCARD_UI] },
  },
  {
    id: 'platform.common-product.close',
    windows: [W.commonProduct, W.commonProductNew],
    label: 'Закрыть',
    summary: 'Закрывает окно товара. Кнопка есть, только когда несохранённых правок нет.',
    effect: 'view',
    evidence: { unit: [CP_SAVE_UI] },
  },
  {
    id: 'platform.common-product.guard-stay',
    windows: [W.commonProduct, W.commonProductNew],
    label: 'Остаться',
    summary: 'Не закрывает окно: можно досохранить или отменить правку.',
    effect: 'view',
    evidence: { unit: [CP_GUARD_UI] },
  },
  {
    id: 'platform.common-product.guard-close',
    windows: [W.commonProduct, W.commonProductNew],
    label: 'Закрыть без сохранения',
    summary: 'Закрывает окно; несохранённые правки пропадают, общий каталог не меняется.',
    effect: 'view',
    evidence: { unit: [CP_GUARD_UI] },
  },
  {
    id: 'platform.common-product.barcode-add',
    windows: [W.commonProduct, W.commonProductNew],
    label: 'Добавить',
    summary: 'Добавляет штрихкод в список товара. Повтор не добавляется. В каталоге штрихкод появится после «Сохранить».',
    effect: 'view',
    disabledWhen: 'Выключена, пока поле штрихкода пустое.',
    evidence: { unit: [CP_BARCODES_UI] },
  },
  {
    id: 'platform.common-product.barcode-remove',
    windows: [W.commonProduct, W.commonProductNew],
    label: 'Удалить',
    summary: 'Убирает штрихкод из списка товара. В каталоге он исчезнет после «Сохранить».',
    effect: 'view',
    evidence: { unit: [CP_BARCODES_UI] },
  },
  {
    id: 'platform.common-product.conflict-take-server',
    windows: [W.commonProduct],
    label: 'Взять текущее',
    summary: 'Принимает значение, которое сейчас в общем каталоге: ваше значение этого поля отбрасывается.',
    effect: 'view',
    evidence: { unit: [CP_CONFLICT_UI] },
  },
  {
    id: 'platform.common-product.conflict-keep-mine',
    windows: [W.commonProduct],
    label: 'Оставить моё',
    summary: 'Оставляет ваше значение поля: при «Сохранить» оно заменит то, что изменил другой человек.',
    effect: 'view',
    evidence: { unit: [CP_CONFLICT_UI] },
  },
  {
    id: 'platform.common-product.open-sync',
    windows: [W.commonProduct],
    label: 'Синхронизация',
    summary: 'Открывает раздел «Синхронизация»: сравнение общего каталога и источников и перенос значений по решению человека.',
    effect: 'view',
    evidence: { unit: [pkg(CP_UI, 'Sources_block_lists_links_and_opens_the_sync_section')] },
  },
  {
    id: 'platform.common-product.source-fields-toggle',
    windows: [W.commonProduct],
    label: 'Все поля источника',
    summary: 'Показывает или прячет все данные, которые источник хранит у этой карточки сверх общего товара. Ничего не меняет.',
    effect: 'view',
    evidence: { unit: [pkg(SP_UI, 'Summary_shows_the_extension_of_each_kind_and_all_fields_expand_on_demand')] },
  },
  {
    id: 'platform.common-product.source-open-card',
    windows: [W.commonProduct],
    label: 'Открыть карточку источника',
    summary: 'Открывает окно этого товара в самом источнике: там правят его данные, которых нет в общем товаре. Общая карточка остаётся открытой.',
    effect: 'view',
    evidence: { unit: [pkg(SP_UI, 'Open_card_button_hands_the_card_window_of_the_source_kind_to_the_opener')] },
  },
  {
    id: 'platform.common-product.archive',
    windows: [W.commonProduct],
    label: 'В архив',
    summary: 'Предлагает отправить товар в архив. Само ничего не меняет: сначала спросит подтверждение.',
    effect: 'view',
    disabledWhen: 'Выключена, пока есть несохранённые правки.',
    evidence: { unit: [CP_ARCHIVE_UI] },
  },
  {
    id: 'platform.common-product.archive-confirm',
    windows: [W.commonProduct],
    label: 'В архив',
    summary:
      'Отправляет товар в архив общего каталога: физически он не удаляется, правка закрывается. В источники уходит только то, что разрешают настройки удаления; по умолчанию — ничего.',
    effect: 'write-local',
    effectText: 'Помечает товар архивным в общем каталоге и пишет журнал.',
    access: CP_WRITE,
    evidence: {
      unit: [CP_ARCHIVE_UI],
      server: [srv(CP_SRV, 'Archive_marks_the_product_archived_and_it_is_no_longer_editable')],
    },
  },
  {
    id: 'platform.common-product.archive-cancel',
    windows: [W.commonProduct],
    label: 'Отмена',
    summary: 'Закрывает вопрос об архиве, товар остаётся как был.',
    effect: 'view',
    evidence: { unit: [CP_ARCHIVE_UI] },
  },
  {
    id: 'platform.photo-crop.accept',
    windows: [W.commonProduct, W.commonProductNew, W.photoAttach],
    label: 'Принять',
    summary:
      'Сохраняет фото по рамке, которая сейчас на экране (если её не трогали — по предложенной): вокруг рамки добавляется небольшой отступ, а кадр делается квадратным белым полем. Предмет не растягивается и не увеличивается. Не получилось — вы остаётесь здесь с причиной и той же рамкой.',
    effect: 'write-local',
    effectText: 'Сохраняет фото временной загрузкой на сервере; в источники оно уходит только кнопкой отправки в окне фото товара.',
    access: CP_WRITE,
    evidence: {
      unit: [PCR_ACCEPT_UI, PCR_DRAG_UI, PCR_FAIL_UI],
      server: [srv('Catalog/CatalogPhotoCropReviewTests.cs', 'Cropped_saves_the_photo_made_by_the_box_of_the_person_as_the_main_one')],
    },
  },
  {
    id: 'platform.photo-crop.cancel',
    windows: [W.commonProduct, W.commonProductNew, W.photoAttach],
    label: 'Отмена',
    summary: 'Закрывает проверку обрезки: фото не загружается. На сервере ничего не сохранено, у товара остаются прежние фото.',
    effect: 'view',
    evidence: { unit: [PCR_CANCEL_UI] },
  },
  {
    id: 'platform.photo-crop.zoom-in',
    windows: [W.commonProduct, W.commonProductNew, W.photoAttach],
    label: 'Увеличить',
    summary: 'Увеличивает фото без фона, чтобы точнее поставить рамку. Двигать увеличенное фото можно пальцем или мышью; то же делают щипок и колесо мыши.',
    effect: 'view',
    disabledWhen: 'Выключена, когда фото уже увеличено до предела.',
    evidence: { unit: [PCR_ZOOM_UI] },
  },
  {
    id: 'platform.photo-crop.zoom-out',
    windows: [W.commonProduct, W.commonProductNew, W.photoAttach],
    label: 'Уменьшить',
    summary: 'Уменьшает фото без фона обратно к целому кадру.',
    effect: 'view',
    disabledWhen: 'Выключена, когда фото уже показано целиком.',
    evidence: { unit: [PCR_ZOOM_UI] },
  },
  {
    id: 'platform.photo-crop.zoom-fit',
    windows: [W.commonProduct, W.commonProductNew, W.photoAttach],
    label: 'По размеру',
    summary: 'Возвращает фото без фона к целому кадру по центру окна. Рамка не меняется.',
    effect: 'view',
    disabledWhen: 'Выключена, когда фото и так показано целиком по центру.',
    evidence: { unit: [PCR_ZOOM_UI] },
  },
  {
    id: 'platform.photo-crop.reset',
    windows: [W.commonProduct, W.commonProductNew, W.photoAttach],
    label: 'Как предложено',
    summary: 'Возвращает рамку туда, где её поставил сервер, и отменяет ваши правки рамки.',
    effect: 'view',
    disabledWhen: 'Выключена, когда рамка и так стоит там, где её предложил сервер.',
    evidence: { unit: [PCR_RESET_UI] },
  },
]



/** Остановки синхронизации (RP-GR10) на вкладке «Ждут решения» окна «Синхронизация»: ответы на строки очереди. */
/** Окно «Синхронизация» (RP-FB50): таблица «источник × сущность × операция» и конфликты приёма. */

/** Вкладка «Схема» окна «Синхронизация» (RP-SY9): полотно React Flow — общий каталог слева, карточки обмена «в источник» и «из источника», источники справа. Ничего не правит. */
const SCAN_UI = 'search/tests/scanVerdict.test.tsx'
const SESSION_UI = 'scan-session/tests/scanSession.test.tsx'
const PICK_UI = 'product-pick/tests/productPick.test.tsx'
const ATTACH_UI = 'common-photos/tests/photoAttach.test.tsx'
const CPS_UI = 'common-product/tests/commonProductScan.test.tsx'
const VARIANTS_UI = 'search/tests/variantsView.test.tsx'
const VARIANTS_CARDS_UI = pkg(VARIANTS_UI, 'Twin_group_shows_variant_cards_side_by_side_with_differences_highlighted_in_each_name')
const VARIANTS_BUTTONS_UI = pkg(VARIANTS_UI, 'Each_variant_has_this_is_it_to_open_and_attach_photo_buttons_and_no_attach_without_the_write_right')
const SCAN_WRITE = 'Тот, кому выдано право менять общий каталог товаров. Без него кнопки не показываются, а сам ответ виден.'
const SCAN_FOUND_UI = pkg(SCAN_UI, 'Found_verdict_shows_big_cards_with_confidence_and_the_confirm_and_open_buttons')
const SCAN_CONFIRM_UI = pkg(SCAN_UI, 'Confirm_buttons_call_confirm_hit_with_the_chosen_product')
const SCAN_NOT_THIS_UI = pkg(SCAN_UI, 'Not_this_hides_the_it_is_he_card_and_keeps_the_other_found_items')
const SCAN_LIST_ALWAYS_UI = pkg(SCAN_UI, 'The_found_list_stays_visible_under_every_verdict_that_has_items')
const SCAN_CANDIDATES_UI = pkg(SCAN_UI, 'Candidates_verdict_lists_tiles_by_confidence_and_none_of_these_leads_to_not_found')
const SCAN_NOT_FOUND_UI = pkg(SCAN_UI, 'Not_found_verdict_says_it_in_words_with_an_icon_and_offers_create_attach_and_retake')
const SCAN_UNDETERMINED_UI = pkg(SCAN_UI, 'Undetermined_verdict_says_it_does_not_mean_the_product_is_absent_and_hides_create_in_the_second_row')
const SCAN_NO_RIGHT_UI = pkg(SCAN_UI, 'Without_the_catalog_write_right_create_and_attach_are_not_shown_but_the_verdict_stays')
const SCAN_WEAK_UI = pkg(SCAN_UI, 'Not_found_verdict_still_lists_the_below_threshold_candidates_open_with_an_honest_caption_and_lets_the_person_pick_one')
const SCAN_WEAK_EVERY_VERDICT_UI = pkg(SCAN_UI, 'The_below_threshold_list_is_shown_under_every_verdict_that_has_weak_candidates')
const ATTACH_FAIL_UI = pkg(ATTACH_UI, 'Attach_upload_failure_shows_the_server_text_with_code_and_offers_retry')
const CPS_DUP_UI = pkg(CPS_UI, 'Duplicate_found_offers_attach_open_and_explicit_create_anyway')
const CPS_DUP_FAIL_UI = pkg(CPS_UI, 'Duplicate_check_failure_asks_before_creating_without_it')
const CPS_PHOTO_FAIL_UI = pkg(CPS_UI, 'Created_but_photo_failed_shows_retry_and_open_without_photo')

/**
 * Сканирование как пополнение базы (RP-FB56): ответ поиска по фото с вердиктом, «Создать новый» и «Прикрепить фото», выбор товара,
 * подтверждение «установить это фото?», смена сканирования. Серверной части у кнопок нет: они открывают окна и пользуются командами
 * общего каталога (RP-FB50), которые уже проверены своими тестами.
 */
/** Окно фото общего товара по источникам (PHI-8): отметки, «Отправить в …», «Похожие», «Загрузить фото». */
const PHR_UI = 'photo-registry/tests/photoRegistry.test.tsx'
const PHR_SEND_UI = pkg(PHR_UI, 'Absent_source_offers_a_send_button_and_present_and_unknown_are_marked_by_icon_and_words')
const PHR_REPLACE_UI = pkg(PHR_UI, 'Source_holding_one_photo_asks_to_confirm_the_replacement_with_the_note_from_the_server')
const PHR_SLIDER_UI = pkg(PHR_UI, 'Slider_shows_one_photo_and_prev_next_and_thumbnails_switch_it')
const PHR_ADD_UI = pkg(PHR_UI, 'Add_photo_flow_shows_crop_then_preview_then_save_and_cancel_leaves_nothing')
const PHR_ADD_FAIL_UI = pkg(PHR_UI, 'Add_photo_failures_are_shown_verbatim_and_can_be_closed')
const PHR_SENDING_UI = pkg(PHR_UI, 'Send_progress_and_the_source_refusal_are_shown_verbatim_with_code_and_copy')
const PHR_PAIR_UI = pkg(PHR_UI, 'Similar_pairs_offer_same_and_different')
const PHR_UPLOAD_UI = pkg(PHR_UI, 'Uploaded_photo_is_marked_as_lying_nowhere_yet_and_the_file_goes_to_the_upload')
const PHR_RELOAD_UI = pkg(PHR_UI, 'Reload_button_rereads_the_photos')
const PHR_DEL_BUTTON_UI = pkg(PHR_UI, 'Delete_button_is_only_at_a_source_that_supports_it_and_only_where_the_photo_lies')
const PHR_DEL_CONFIRM_UI = pkg(PHR_UI, 'Source_delete_asks_for_confirmation_that_names_what_and_where_and_runs_only_after_it')
const PHR_DEL_ONLY_UI = pkg(PHR_UI, 'Source_delete_warns_when_it_is_the_only_place_the_photo_lies')
const PHR_DEL_STAGED_UI = pkg(PHR_UI, 'Staged_photo_is_removed_after_confirmation_that_says_it_lies_only_with_us')
const PHR_DEL_FAIL_UI = pkg(PHR_UI, 'Delete_refusal_and_unknown_outcome_are_verbatim_and_retry_repeats_the_same_action')
const PHR_DEL_STAGED_FAIL_UI = pkg(PHR_UI, 'Staged_remove_refusal_is_verbatim_and_retry_repeats_the_removal')
const PHR_DEL_RUNNING_UI = pkg(PHR_UI, 'Running_delete_disables_its_button_and_says_so')
const PHR_UNKNOWN_UI = pkg(PHR_UI, 'Unreadable_source_says_unknown_not_absent_and_shows_the_server_text')

const PHOTO_REGISTRY: readonly ActionDescriptor[] = [
  {
    id: 'platform.photo-registry.reload',
    windows: [W.commonProduct],
    label: 'Обновить',
    summary: 'Заново читает список фотографий товара и отметки по источникам.',
    effect: 'read',
    evidence: { unit: [PHR_RELOAD_UI] },
  },
  {
    id: 'platform.photo-registry.upload',
    windows: [W.commonProduct],
    label: 'Добавить фото',
    summary:
      'Берёт снимок или файл, убирает фон и предлагает рамку. Дальше вы правите рамку, смотрите результат и нажимаете «Сохранить»: до этого на сервере ничего не лежит. Сохранённое фото нигде пока не лежит — в источник оно уходит только по кнопке «Отправить в …».',
    effect: 'read',
    disabledWhen: 'Выключена, пока идёт добавление предыдущего фото.',
    evidence: { unit: [PHR_UPLOAD_UI, PHR_ADD_UI] },
  },
  {
    id: 'platform.photo-registry.add-preview',
    windows: [W.commonProduct],
    label: 'Показать результат',
    summary: 'Принимает рамку, которая сейчас на экране, и показывает, каким получится фото. Ничего не сохраняет.',
    effect: 'read',
    evidence: { unit: [PHR_ADD_UI] },
  },
  {
    id: 'platform.photo-registry.add-edit',
    windows: [W.commonProduct],
    label: 'Править рамку',
    summary: 'Возвращает от результата к рамке: она остаётся там, где вы её поставили.',
    effect: 'view',
    disabledWhen: 'Выключена, пока идёт сохранение.',
    evidence: { unit: [PHR_ADD_UI] },
  },
  {
    id: 'platform.photo-registry.add-save',
    windows: [W.commonProduct],
    label: 'Сохранить',
    summary:
      'Кладёт фото по вашей рамке и исходный файл во временную загрузку нашего сервера. Фото появится в списке с пометкой «Пока нигде не лежит» и уйдёт в источник только по кнопке «Отправить в …». Хранится семь суток, после первой отправки удаляется.',
    effect: 'write-local',
    effectText: 'Кладёт фото и исходный файл во временное хранилище нашего сервера.',
    access: CP_WRITE,
    disabledWhen: 'Выключена, пока идёт сохранение.',
    evidence: { unit: [PHR_ADD_UI, PHR_ADD_FAIL_UI] },
  },
  {
    id: 'platform.photo-registry.add-cancel',
    windows: [W.commonProduct],
    label: 'Отменить',
    summary: 'Закрывает добавление фото: на сервере ничего не сохранено, у товара остаются прежние фото. Так же закрывается сообщение об отказе.',
    effect: 'view',
    disabledWhen: 'Выключена, пока идёт сохранение.',
    evidence: { unit: [PHR_ADD_UI, PHR_ADD_FAIL_UI] },
  },
  {
    id: 'platform.photo-registry.prev',
    windows: [W.commonProduct],
    label: 'Предыдущее фото',
    summary: 'Показывает предыдущее фото товара. То же делает стрелка влево на клавиатуре.',
    effect: 'view',
    disabledWhen: 'Выключена на первом фото.',
    evidence: { unit: [PHR_SLIDER_UI] },
  },
  {
    id: 'platform.photo-registry.next',
    windows: [W.commonProduct],
    label: 'Следующее фото',
    summary: 'Показывает следующее фото товара. То же делает стрелка вправо на клавиатуре.',
    effect: 'view',
    disabledWhen: 'Выключена на последнем фото.',
    evidence: { unit: [PHR_SLIDER_UI] },
  },
  {
    id: 'platform.photo-registry.show-photo',
    windows: [W.commonProduct],
    label: 'Показать фото',
    summary: 'Открывает это фото крупно и показывает, в каких источниках оно лежит.',
    effect: 'view',
    evidence: { unit: [PHR_SLIDER_UI] },
  },
  {
    id: 'platform.photo-registry.send',
    windows: [W.commonProduct],
    label: 'Отправить в источник',
    summary:
      'Просит службу синхронизации положить это фото в карточку товара у выбранного источника. Сама ничего не отправляет: только по этой кнопке. Ход и отказ источника видны под кнопкой. Кнопка есть только у источников, которые принимают фото.',
    effect: 'write-external',
    effectText: 'Добавляет фото в карточку товара у выбранного источника.',
    access: 'Тот, кому выдано право менять общий каталог товаров и применять изменения в «Синхронизации», с допуском к этому источнику.',
    disabledWhen: 'Выключена, пока отправка уже идёт или источник не прочитан и неизвестно, есть ли там это фото.',
    evidence: { unit: [PHR_SEND_UI, PHR_SENDING_UI, PHR_UNKNOWN_UI] },
  },
  {
    id: 'platform.photo-registry.replace-confirm',
    windows: [W.commonProduct],
    label: 'Заменить',
    summary: 'Подтверждает замену: источник держит одну картинку, и отправка заменит стоящую этой. Прежняя картинка у источника пропадёт.',
    effect: 'write-external',
    effectText: 'Заменяет картинку товара у источника.',
    access: 'Тот, кому выдано право менять общий каталог товаров и применять изменения в «Синхронизации», с допуском к этому источнику.',
    evidence: { unit: [PHR_REPLACE_UI] },
  },
  {
    id: 'platform.photo-registry.replace-cancel',
    windows: [W.commonProduct],
    label: 'Отмена',
    summary: 'Закрывает вопрос о замене: ничего не отправляется, стоящая картинка остаётся.',
    effect: 'view',
    evidence: { unit: [PHR_REPLACE_UI] },
  },
  {
    id: 'platform.photo-registry.send-dismiss',
    windows: [W.commonProduct],
    label: 'Закрыть',
    summary: 'Убирает сообщение об отказе отправки. Фото при этом не отправлено; можно нажать «Отправить» ещё раз.',
    effect: 'view',
    evidence: { unit: [PHR_SENDING_UI] },
  },
  {
    id: 'platform.photo-registry.delete-source',
    windows: [W.commonProduct],
    label: 'Удалить из источника',
    summary:
      'Просит службу синхронизации удалить это фото из карточки товара у выбранного источника. Сначала спрашивает подтверждение: что удаляется и откуда. Кнопка есть только там, где фото лежит и источник умеет удалять; у AQSI её нет — удалите фото в кабинете.',
    effect: 'write-external',
    effectText: 'Удаляет фото из карточки товара у выбранного источника.',
    access: 'Тот, кому выдано право менять общий каталог товаров и применять изменения в «Синхронизации», с допуском к этому источнику.',
    disabledWhen: 'Выключена, пока удаление или отправка уже идёт.',
    evidence: { unit: [PHR_DEL_BUTTON_UI, PHR_DEL_CONFIRM_UI, PHR_DEL_ONLY_UI, PHR_DEL_RUNNING_UI] },
  },
  {
    id: 'platform.photo-registry.remove-staged',
    windows: [W.commonProduct],
    label: 'Убрать загруженное',
    summary:
      'Убирает фото, которое вы загрузили и которое пока нигде не лежит: снимок и исходный файл на нашем сервере. Сначала спрашивает подтверждение. В источниках ничего не меняется.',
    effect: 'write-local',
    effectText: 'Удаляет временную загрузку (снимок и исходный файл) с нашего сервера.',
    access: CP_WRITE,
    disabledWhen: 'Выключена, пока удаление уже идёт.',
    evidence: { unit: [PHR_DEL_STAGED_UI] },
  },
  {
    id: 'platform.photo-registry.delete-confirm',
    windows: [W.commonProduct],
    label: 'Удалить / Убрать',
    summary:
      'Подтверждает удаление: фото убирается из выбранного источника либо, у загруженного, с нашего сервера. Вернуть удалённое нельзя — фото придётся добавить заново.',
    effect: 'write-external',
    effectText: 'Удаляет фото у источника либо временную загрузку с нашего сервера.',
    access: 'Тот, кому выдано право менять общий каталог товаров; для источника ещё право применять изменения в «Синхронизации» и допуск к нему.',
    evidence: { unit: [PHR_DEL_CONFIRM_UI, PHR_DEL_STAGED_UI] },
  },
  {
    id: 'platform.photo-registry.delete-cancel',
    windows: [W.commonProduct],
    label: 'Отмена',
    summary: 'Закрывает вопрос об удалении: ничего не удаляется, фото остаётся на месте.',
    effect: 'view',
    evidence: { unit: [PHR_DEL_CONFIRM_UI, PHR_DEL_STAGED_UI] },
  },
  {
    id: 'platform.photo-registry.delete-retry',
    windows: [W.commonProduct],
    label: 'Повторить',
    summary:
      'Повторяет то же удаление после отказа или когда ответ не пришёл. Ключ операции тот же, поэтому второго удаления не будет: если первое прошло, источник это узнает.',
    effect: 'write-external',
    effectText: 'Повторно просит удалить фото у источника либо убрать временную загрузку.',
    access: 'Тот, кому выдано право менять общий каталог товаров; для источника ещё право применять изменения в «Синхронизации» и допуск к нему.',
    evidence: { unit: [PHR_DEL_FAIL_UI, PHR_DEL_STAGED_FAIL_UI] },
  },
  {
    id: 'platform.photo-registry.delete-dismiss',
    windows: [W.commonProduct],
    label: 'Закрыть',
    summary: 'Убирает сообщение об удалении. Если удаление не прошло, фото остаётся как было; можно нажать «Удалить» ещё раз.',
    effect: 'view',
    evidence: { unit: [PHR_DEL_FAIL_UI, PHR_DEL_STAGED_FAIL_UI] },
  },
  {
    id: 'platform.photo-registry.same',
    windows: [W.commonProduct],
    label: 'Это одно',
    summary: 'Записывает ваше решение: две похожие фотографии — одна и та же. Они сольются в одну, решение переживает любую пересборку списка.',
    effect: 'write-local',
    effectText: 'Записывает решение о паре фотографий в нашей базе; в источники ничего не уходит.',
    access: CP_WRITE,
    evidence: { unit: [PHR_PAIR_UI] },
  },
  {
    id: 'platform.photo-registry.different',
    windows: [W.commonProduct],
    label: 'Разные',
    summary: 'Записывает ваше решение: две похожие фотографии — разные. Такую пару больше не предлагаем.',
    effect: 'write-local',
    effectText: 'Записывает решение о паре фотографий в нашей базе; в источники ничего не уходит.',
    access: CP_WRITE,
    evidence: { unit: [PHR_PAIR_UI] },
  },
]

const SCAN: readonly ActionDescriptor[] = [
  {
    id: 'platform.scan.open-hit',
    windows: [W.search, W.scanner],
    label: 'Открыть',
    summary: 'Открывает карточку общего товара («Только открыть» у карточки «Это он?» или нажатие на плашку списка найденного), ничего не запоминая. В счётчике смены товар идёт в «Найдено».',
    effect: 'view',
    evidence: { unit: [SCAN_FOUND_UI, SCAN_CANDIDATES_UI] },
  },
  {
    id: 'platform.scan.confirm-hit',
    windows: [W.search, W.scanner],
    label: 'Да, это он',
    summary:
      'Подтверждает, что снимок — именно этот товар. Система запоминает снимок за ним: фото прикрепляется к товару, а прочитанный на нём текст становится поисковыми фразами товара, и в следующий раз он находится лучше. Без права менять каталог кнопка просто открывает карточку.',
    effect: 'write-local',
    effectText: 'Прикрепляет снимок к общему товару и сохраняет прочитанный текст как поисковые фразы этого товара (до 15 на товар, редкие вытесняются).',
    access: SCAN_WRITE,
    disabledWhen: 'Без права менять общий каталог ничего не записывается: открывается только карточка товара.',
    evidence: {
      unit: [SCAN_CONFIRM_UI, SCAN_FOUND_UI, SCAN_CANDIDATES_UI, SCAN_NO_RIGHT_UI],
      server: [
        srv('Search/SearchConfirmTests.cs', 'Confirmation_stores_normalized_phrases_of_the_chosen_product_with_counters'),
        srv('Search/SearchConfirmTests.cs', 'Repeated_operation_id_is_not_counted_twice'),
        srv('Search/SearchConfirmTests.cs', 'Phrase_limit_evicts_the_rarest_phrases_but_keeps_the_new_ones'),
      ],
    },
  },
  {
    id: 'platform.scan.confirm-weak',
    windows: [W.search, W.scanner],
    label: 'Прикрепить фото к нему',
    summary:
      'Кнопка у кандидата из списка «Ниже порога — вероятнее всего не он»: человек сам выбирает товар, который система отсекла порогом. Действует как «Да, это он»: снимок запоминается за этим товаром (фото прикрепляется, прочитанный текст становится поисковыми фразами). Без права менять каталог кнопка называется «Открыть его» и только открывает карточку.',
    effect: 'write-local',
    effectText: 'Прикрепляет снимок к выбранному общему товару и сохраняет прочитанный текст как поисковые фразы этого товара.',
    access: SCAN_WRITE,
    disabledWhen: 'Без права менять общий каталог ничего не записывается: открывается только карточка товара.',
    evidence: {
      unit: [SCAN_WEAK_UI, SCAN_WEAK_EVERY_VERDICT_UI, SCAN_NO_RIGHT_UI, VARIANTS_BUTTONS_UI],
      server: [srv('Search/SearchConfirmTests.cs', 'Confirmation_stores_normalized_phrases_of_the_chosen_product_with_counters')],
    },
  },
  {
    id: 'platform.scan.variant-pick',
    windows: [W.search, W.scanner],
    label: 'Это он',
    summary:
      'Кнопка у варианта экрана «Выберите вариант»: открывает карточку этого товара. Выбор делает человек, система за него вариант не выбирает и ничего не запоминает; прикрепить к варианту фото можно отдельной кнопкой «Прикрепить фото к нему».',
    effect: 'view',
    evidence: { unit: [VARIANTS_CARDS_UI, VARIANTS_BUTTONS_UI] },
  },
  {
    id: 'platform.scan.not-this',
    windows: [W.search, W.scanner],
    label: 'Нет, другой',
    summary: 'Прячет подсказку «Это он?»: остальные найденные товары остаются в списке ниже. Ничего не записывает.',
    effect: 'view',
    evidence: { unit: [SCAN_NOT_THIS_UI, SCAN_LIST_ALWAYS_UI] },
  },
  {
    id: 'platform.scan.none-of-these',
    windows: [W.search, W.scanner],
    label: 'Ничего из этого',
    summary: 'Отклоняет предложенные варианты: экран становится «Нет в базе», где можно создать новый товар или прикрепить фото к другому (найденное остаётся списком под ним). Ничего не записывает.',
    effect: 'view',
    evidence: { unit: [SCAN_CANDIDATES_UI, VARIANTS_CARDS_UI] },
  },
  {
    id: 'platform.scan.create-new',
    windows: [W.search, W.scanner],
    label: 'Создать новый товар',
    summary:
      'Открывает окно «Новый общий товар» с уже подставленными названием, штрихкодом и снимком. Товар появится в общем каталоге только после «Создать товар» и проверки, нет ли такого же.',
    effect: 'view',
    access: SCAN_WRITE,
    disabledWhen: 'Кнопки нет у того, кому не выдано право менять общий каталог.',
    evidence: { unit: [SCAN_NOT_FOUND_UI, SCAN_NO_RIGHT_UI] },
  },
  {
    id: 'platform.scan.find-and-attach',
    windows: [W.search, W.scanner],
    label: 'Найти товар и прикрепить фото',
    summary:
      'Открывает список общего каталога в режиме выбора: найдите товар, которому нужно это фото. Дальше система спросит подтверждение, и только после него фото запишется.',
    effect: 'view',
    access: SCAN_WRITE,
    disabledWhen: 'Кнопки нет у того, кому не выдано право менять общий каталог.',
    evidence: { unit: [SCAN_NOT_FOUND_UI, SCAN_UNDETERMINED_UI] },
  },
  {
    id: 'platform.scan.create-anyway',
    windows: [W.search, W.scanner],
    label: 'Создать всё равно',
    summary:
      'Открывает окно нового товара, хотя система не смогла определить, есть ли такой товар. Перед записью она ещё раз поищет похожий. Используйте, только если уверены.',
    effect: 'view',
    access: SCAN_WRITE,
    disabledWhen: 'Кнопки нет у того, кому не выдано право менять общий каталог.',
    evidence: { unit: [SCAN_UNDETERMINED_UI] },
  },
  {
    id: 'platform.scan.retake',
    windows: [W.search, W.scanner],
    label: 'Снять ещё раз',
    summary: 'В сканере прячет ответ и возвращает камеру; на экране поиска открывает выбор другого фото. Кадр попадает в «Пропущено» смены.',
    effect: 'view',
    evidence: { unit: [SCAN_NOT_FOUND_UI, SCAN_UNDETERMINED_UI, pkg(SCAN_UI, 'Retake_tells_the_session_it_was_skipped_and_calls_the_screen')] },
  },
  {
    id: 'platform.scan.session-toggle',
    windows: [W.search, W.scanner],
    label: 'Смена сканирования',
    summary:
      'Строка «Создано · Прикреплено · Найдено · Пропущено» — что сделано за эту смену. Нажатие раскрывает ленту: название, время и что сделано. Это счётчик смены, а не журнал: с закрытием сканера он обнуляется.',
    effect: 'view',
    evidence: {
      unit: [
        pkg(SESSION_UI, 'Session_bar_shows_counters_in_words_and_the_last_event'),
        pkg(SESSION_UI, 'Session_log_opens_on_press_and_lists_events_with_open_for_products'),
      ],
    },
  },
  {
    id: 'platform.scan.session-open',
    windows: [W.scanner],
    label: 'Открыть',
    summary: 'В ленте смены открывает карточку товара, который был создан, найден или получил фото.',
    effect: 'view',
    evidence: { unit: [pkg(SESSION_UI, 'Session_log_opens_on_press_and_lists_events_with_open_for_products')] },
  },
  {
    id: 'platform.scan.photo-cancel',
    windows: [W.commonProductNew, W.photoAttach],
    label: 'Отменить',
    summary: 'Останавливает загрузку снимка. Если товар уже создан, он остаётся без фото; фото можно добавить в его карточке.',
    effect: 'view',
    evidence: { unit: [ATTACH_FAIL_UI] },
  },
  {
    id: 'platform.scan.photo-retry',
    windows: [W.commonProductNew, W.photoAttach],
    label: 'Повторить',
    summary: 'Отправляет тот же снимок ещё раз с тем же ключом операции: если прошлая попытка всё же дошла, второго фото не будет.',
    effect: 'write-local',
    effectText: 'Сохраняет фото в собственном хранилище общего каталога.',
    access: CP_WRITE,
    evidence: { unit: [ATTACH_FAIL_UI, CPS_PHOTO_FAIL_UI] },
  },
  {
    id: 'platform.scan.photo-close',
    windows: [W.commonProductNew, W.photoAttach],
    label: 'Закрыть',
    summary: 'Убирает сообщение о неудавшейся загрузке; фото не добавляется.',
    effect: 'view',
    evidence: { unit: [ATTACH_FAIL_UI] },
  },
  {
    id: 'platform.product-pick.pick',
    windows: [W.productPick],
    label: 'Выбрать',
    summary: 'Выбирает этот товар и открывает подтверждение «Для товара … установить это фото?». Ничего не записывает.',
    effect: 'view',
    evidence: { unit: [pkg(PICK_UI, 'Choosing_a_row_calls_pick_with_that_product')] },
  },
  {
    id: 'platform.product-pick.page',
    windows: [W.productPick],
    label: 'Страница списка',
    summary: 'Переходит на предыдущую или следующую страницу списка общего каталога.',
    effect: 'view',
    disabledWhen: 'Выключена на первой странице («Назад») и на последней («Дальше»).',
    evidence: { unit: [pkg(PICK_UI, 'The_picker_pages_through_the_list_and_search_goes_back_to_the_first_page')] },
  },
  {
    id: 'platform.product-pick.close',
    windows: [W.productPick],
    label: 'Отмена',
    summary: 'Закрывает выбор, ничего не выбирая и не записывая; вы возвращаетесь туда, откуда пришли.',
    effect: 'view',
    evidence: { unit: [pkg(PICK_UI, 'Picker_shows_the_frame_and_the_read_name_and_search_is_prefilled_by_the_binding')] },
  },
  {
    id: 'platform.photo-attach.save',
    windows: [W.photoAttach],
    label: 'Сохранить',
    summary:
      'Кладёт снимок временной загрузкой выбранного товара (подготовка, проверка обрезки, сохранение). Фото остаётся у нас и нигде не лежит, пока его не отправят кнопкой в окне фото товара. После успеха окно закрывается, а в смене сканирования появляется «Фото прикреплено к …».',
    effect: 'write-local',
    effectText: 'Сохраняет фото временной загрузкой на сервере; в источники оно уходит только кнопкой отправки в окне фото товара.',
    access: CP_WRITE,
    disabledWhen: 'Выключена, пока читается товар, нет снимка (страницу обновили), нет права менять каталог или идёт загрузка.',
    evidence: {
      unit: [
        pkg(ATTACH_UI, 'Attach_save_starts_the_staging_upload_without_a_mode'),
        pkg(ATTACH_UI, 'Attach_without_the_right_or_the_frame_blocks_saving_with_the_reason'),
      ],
    },
  },
  {
    id: 'platform.photo-attach.cancel',
    windows: [W.photoAttach],
    label: 'Отмена',
    summary: 'Закрывает окно, ничего не записывая; вы возвращаетесь туда, откуда пришли.',
    effect: 'view',
    disabledWhen: 'Выключена, пока идёт загрузка: сначала «Отменить» загрузку.',
    evidence: { unit: [pkg(ATTACH_UI, 'Attach_screen_asks_for_the_product_and_shows_the_new_frame')] },
  },
  {
    id: 'platform.common-product.duplicate-attach',
    windows: [W.commonProductNew],
    label: 'Это он — прикрепить фото',
    summary: 'Находка и есть нужный товар: открывает подтверждение «Для товара … установить это фото?», новый товар не создаётся.',
    effect: 'view',
    evidence: { unit: [CPS_DUP_UI] },
  },
  {
    id: 'platform.common-product.duplicate-open',
    windows: [W.commonProductNew],
    label: 'Открыть',
    summary: 'Открывает карточку найденного похожего товара вместо создания нового.',
    effect: 'view',
    evidence: { unit: [CPS_DUP_UI] },
  },
  {
    id: 'platform.common-product.duplicate-back',
    windows: [W.commonProductNew],
    label: 'Назад к форме',
    summary: 'Возвращает к форме нового товара, ничего не создавая: можно поправить название или выбрать другой путь.',
    effect: 'view',
    evidence: { unit: [CPS_DUP_UI, CPS_DUP_FAIL_UI] },
  },
  {
    id: 'platform.common-product.duplicate-create-anyway',
    windows: [W.commonProductNew],
    label: 'Всё равно создать',
    summary:
      'Создаёт новый товар, несмотря на найденный похожий (или несмотря на то, что проверку сделать не удалось). Получится ещё одна карточка; потом такие пары разбирают в «Дублях».',
    effect: 'write-local',
    effectText: 'Создаёт общий товар в нашей базе; в источники он уходит только по настройкам синхронизации.',
    access: CP_WRITE,
    evidence: { unit: [CPS_DUP_UI, CPS_DUP_FAIL_UI] },
  },
  {
    id: 'platform.common-product.open-without-photo',
    windows: [W.commonProductNew],
    label: 'Открыть товар без фото',
    summary: 'Товар уже создан, а снимок не загрузился: открывает карточку товара как есть, фото можно добавить в ней позже.',
    effect: 'view',
    evidence: { unit: [CPS_PHOTO_FAIL_UI] },
  },
]


/** Вкладка «Правила» окна «Синхронизация» (RP-SY4): строка на поле, короткое окно «Что делать → Вы уверены?», «что изменится», журнал. */

/** Экран «Кто главный» (веса источников): порядок источников по полям и режим «вручную / автоматически». */

/** «Ждут решения» одним списком (SYNC-UI-SPEC §4) и источник-эталон с «Сделать всё как в эталоне» (§9) — окно «Синхронизация». */
/** Выбор, отметки и раскрывающиеся блоки экранов синхронизации, правил и карточек: у каждого своя подсказка ⓘ (UI-ACTIONS). */
const SCREEN_CONTROLS: readonly ActionDescriptor[] = [
  {
    id: 'platform.common-product.cash-field',
    windows: [W.commonProduct],
    label: 'Кассовое поле товара',
    summary: 'Выбор значения кассового поля товара (ставка НДС и другие). Не выбрано — поле остаётся неизвестным, и своя касса не продаст товар. Записывается кнопкой «Сохранить».',
    effect: 'view',
    evidence: { unit: [pkg('common-product/tests/cashFields.test.tsx', 'Choosing_a_vat_rate_enables_save_and_the_note_names_the_unknown_value_it_had_before')] },
  },
  {
    id: 'platform.resource.module-rights',
    windows: [W.resource],
    label: 'Права модуля',
    summary: 'Раскрывает список прав модуля источника и показывает, какие из них включены. Ничего не меняет.',
    effect: 'view',
    evidence: { unit: [pkg('resources/tests/moduleState.test.tsx', 'Modules_come_from_the_manifest_with_words_and_the_rights_of_this_resource_and_say_when_the_server_reports_nothing')] },
  },
  {
    id: 'platform.scan.why',
    windows: [W.search, W.scanner],
    label: 'Из чего сложилось',
    summary: 'Раскрывает, из чего сложилась уверенность в найденном товаре: что совпало по фото, тексту и коду. Ничего не меняет.',
    effect: 'view',
    evidence: { unit: [SCAN_CANDIDATES_UI] },
  },
]

export const PLATFORM_ACTIONS: readonly ActionDescriptor[] = [
  ...SCREEN_CONTROLS,
  ...RMK_SOURCES,
  ...RECEIPTS_SOURCES,
  ...PRICES_SOURCES,
  ...SECTION_SOURCES,
  ...RESOURCES,
  ...SOURCE_ADD,
  ...RESOURCE_CARD,
  ...RESOURCE_EDITOR,
  ...ACCESS,
  ...DIAGNOSTICS_AND_ABOUT,
  ...HARDWARE,
  ...LINKS,
  ...SEARCH,
  ...COMMON_CATALOG,
  ...NOMENCLATURE_SETTINGS,
  ...COMMON_PRODUCT,
  ...PHOTO_REGISTRY,
  ...SCAN,
  ...TESTING,
]
